import React, { useEffect, useState, useRef, useCallback } from "react";
import "./index.scss";
import { Card, Col, Divider, Row, Tabs, Progress, Button, Modal, message, Spin, Tooltip, Image, Select, Input, Breadcrumb, Empty } from "antd";
import { CompassOutlined, RedoOutlined, LoadingOutlined, HistoryOutlined, DeleteOutlined, ArrowRightOutlined, ArrowLeftOutlined, EyeOutlined, EditOutlined, InfoCircleOutlined, UnorderedListOutlined, FileOutlined, AppstoreOutlined, FileTextOutlined, CheckCircleOutlined, SearchOutlined, CloseCircleOutlined } from "@ant-design/icons";
import InfiniteScrollGallery from "../CustomerComponents/TwoDImagesPage/InfiniteScrollGallery";
import AIEditorModalViewer from "../CustomerComponents/AIEditorModalViewer/AIEditorModalViewer";
import CompareSiloView from "../CustomerComponents/AIEditorModalViewer/CompareSiloView";
import GenerateAiVideoModal from "../CustomerComponents/GenerateAiVideoModal/GenerateAiVideoModal";
import ENVIRONMENT from "../../../environments";
import AiLoader from "./AiLoader";
import axios from "axios";
import pako from "pako";
import {CustomImage as CustomImage} from "../../Common/CustomImage/CustomImage";
import { getDisplayEngineName, getEngineLogo, getEngineCssClass } from "../Utilities";
import playIconSvg from "../../../assets/images/play-circle-icon.svg";

const { TabPane } = Tabs;
const { PreviewGroup } = Image;
const CUSTOMER_USERNAME = localStorage.getItem("username");

const antIcon = (
    <LoadingOutlined
      style={{
        fontSize: 24,
        color: "#276DD7",
      }}
      spin
    />
  );

const TabContent = React.memo(({ images, loading }) => {
    return (
    <InfiniteScrollGallery
    type="ai_explore"
        images={images}
        loading={loading}
        useAntdPreview={true}
    />
    );
});

function getGridIcon(color = '#276DD7') {
    // We use a template literal (backticks ``) to inject the 'color' variable
    return `
      <svg width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M13.3333 11.3333C13.3333 11.5101 13.2631 11.6797 13.1381 11.8047C13.013 11.9298 12.8435 12 12.6667 12H0.666667C0.489856 12 0.320286 11.9298 0.195262 11.8047C0.0702379 11.6797 0 11.5101 0 11.3333V0.666667C0 0.489856 0.0702379 0.320286 0.195262 0.195262C0.320286 0.0702379 0.489856 0 0.666667 0H12.6667C12.8435 0 13.013 0.0702379 13.1381 0.195262C13.2631 0.320286 13.3333 0.489856 13.3333 0.666667V11.3333ZM6 8H1.33333V10.6667H6V8ZM12 5.33333H7.33333V10.6667H12V5.33333ZM6 1.33333H1.33333V6.66667H6V1.33333ZM12 1.33333H7.33333V4H12V1.33333Z" 
              fill="${color}" />
      </svg>
    `;
  }

  const getImageOrientation = (width, height) => {
    if (!width || !height) return 'square';
    if (width > height) return 'landscape';
    if (height > width) return 'portrait';
    return 'square';
  };

// Toggle this flag to switch between dynamic Explore (API-backed) and static Explore (existing props-based)
const USE_DYNAMIC_EXPLORE = false;

const ActivityAndExploreSection = (props) => {
    const [regenerateRequestLoaderId, setRegenerateRequestLoaderId] = useState(null);
    const [removeRequestLoaderId, setRemoveRequestLoaderId] = useState(null);
    const [submitForReviewLoaderId, setSubmitForReviewLoaderId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedImageData, setSelectedImageData] = useState(null);
    const [compareSiloModalOpen, setCompareSiloModalOpen] = useState(false);
    const [hoveredTabKey, setHoveredTabKey] = useState(null);
    const [editProductNameModalOpen, setEditProductNameModalOpen] = useState(false);
    const [productNameDraft, setProductNameDraft] = useState('');
    const [productNameSaving, setProductNameSaving] = useState(false);
    const [exploreImages, setExploreImages] = useState([]);
    const [exploreLoading, setExploreLoading] = useState(false);

    const resolvedActiveTabKey = ENVIRONMENT.COMPANY_IDS_WITH_CUSTOM_AI_OUTPUTS.includes(localStorage.getItem("company_id"))
        ? 'ai-created-images'
        : props.activeTabKey;
    const [imageDisplayLoader, setImageDisplayLoader] = useState(false);
    const [deleteModal, setDeleteModal] = useState();
    const [generateVideoModalOpen, setGenerateVideoModalOpen] = useState(false);
    const [selectedVideoData, setSelectedVideoData] = useState(null);
    const [deleteLoader, setDeleteLoader] = useState(false);
    const lastItemRef = useRef(null);
    const sectionTopRef = useRef(null);
    const [scrollPositions, setScrollPositions] = useState({});
    const rowRefs = useRef({});
    const [previewGroupVisible, setPreviewGroupVisible] = useState({});
    const previewImageRefs = useRef({});
    const [expandedFilterKey, setExpandedFilterKey] = useState('all');
    const [collaborateLoader, setCollaborateLoader] = useState(false);
    const { Option } = Select;
    const searchTimeoutRef = useRef(null);

    // Lazy loading state for horizontal image rows
    const [visibleRendersCount, setVisibleRendersCount] = useState({});
    const INITIAL_VISIBLE_COUNT = 5;
    const LOAD_MORE_COUNT = 5;

    // Row virtualization state - track which rows are visible
    const [visibleRows, setVisibleRows] = useState(new Set());
    const rowObserverRefs = useRef({});
    const ROW_BUFFER = 2; // Render 2 extra rows above and below visible area
    const ESTIMATED_ROW_HEIGHT = 400; // Estimated height for placeholder rows

    // Helper to get visible render count for a row
    const getVisibleRenderCount = (rowIndex) => {
        return visibleRendersCount[rowIndex] || INITIAL_VISIBLE_COUNT;
    };

    // Load more renders for a specific row
    const loadMoreRenders = (rowIndex, totalRenders) => {
        const currentVisible = getVisibleRenderCount(rowIndex);
        if (currentVisible < totalRenders) {
            setVisibleRendersCount(prev => ({
                ...prev,
                [rowIndex]: Math.min(currentVisible + LOAD_MORE_COUNT, totalRenders)
            }));
        }
    };

    // Search filtering logic with render-level filtering
    const filterItemsBySearch = (items, query) => {
        if (!query || query.trim() === '') {
            return items;
        }
        
        const lowerQuery = query.toLowerCase().trim();
        const filteredItems = [];
        
        items.forEach((item) => {
            // Check if product_name matches
            const productName = String(item?.product_name || '').toLowerCase();
            const productNameMatches = productName.includes(lowerQuery);
            
            // Check if product_id matches
            const productId = String(item?.product_id || '').toLowerCase();
            const productIdMatches = productId.includes(lowerQuery);
            
            // Check if category matches
            const category = String(item?.category || '').toLowerCase();
            const categoryMatches = category.includes(lowerQuery);
            
            // Find matching renders by display_name
            const matchingRenders = (item?.renders || []).filter(render => {
                const displayName = String(render?.display_name || '').toLowerCase();
                return displayName.includes(lowerQuery);
            });
            
            const hasMatchingRenders = matchingRenders.length > 0;
            
            // Apply the two rules:
            // Rule 1: Product name/ID/category matches → show all renders
            if (productNameMatches || productIdMatches || categoryMatches) {
                filteredItems.push({
                    ...item,
                    filteredRenders: item.renders || []
                });
            }
            // Rule 2: Product name/ID/category don't match BUT render display_name matches → show only matching renders
            else if (hasMatchingRenders) {
                filteredItems.push({
                    ...item,
                    filteredRenders: matchingRenders
                });
            }
        });
        
        return filteredItems;
    };

    // Handle search with debounce and trigger fetch if needed
    const handleSearchChange = (e) => {
        const value = e.target.value;
        props.setSearchQuery(value);
        
        // Clear existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        // Debounce: wait 500ms after user stops typing
        searchTimeoutRef.current = setTimeout(() => {
            if (value.trim() !== '' && !props.allDataFetchedForSearch && !props.isSearchFetchingAll) {
                // Trigger fetch all data in background
                props.fetchAllRemainingDataForSearch();
            }
        }, 500);
    };

    const handleClearSearch = () => {
        props.setSearchQuery('');
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
    };

    // Filtered activity images based on search
    const filteredActivityImages = props.searchQuery && props.searchQuery.trim() !== '' 
        ? filterItemsBySearch(props.activity_images || [], props.searchQuery)
        : props.activity_images;

    // Helper to get the correct renders array (filtered or all)
    const getRendersList = (item) => {
        // When searching, use filteredRenders if it exists
        if (props.searchQuery && props.searchQuery.trim() !== '' && item.filteredRenders) {
            return item.filteredRenders;
        }
        // Otherwise use all renders
        return item.renders || [];
    };

    const getPropImageUris = (item) => {
    // Prefer new backend shape: item.props = [{ image_uri, thumbnail_uri, height, width, depth, ... }]
    if (item?.props && Array.isArray(item.props)) {
        const data = item.props.map(p => ({ 
            image_uri: p?.image_uri, 
            thumbnail_uri: p?.thumbnail_uri 
        })).filter(p => p.image_uri);
        if (data.length > 0) return data;
    }
    // Backward compat: older records may still provide props_image_uris
    return (item?.props_image_uris || []).map(uri => ({ image_uri: uri }));
};

    const expandedJobId = props.expandedJobId || "";
    const expandedItem = expandedJobId
        ? (props.activity_images || []).find(req =>
            String(req?.job_id ?? "") === String(expandedJobId)
          )
        : null;

    const expandedProductName = (expandedItem?.product_name && String(expandedItem.product_name).trim().length > 0)
        ? String(expandedItem.product_name).trim()
        : 'Untitled';

    useEffect(() => {
        // Reset filter when switching expanded jobs (or leaving expanded mode)
        if (expandedJobId) setExpandedFilterKey('all');
    }, [expandedJobId]);

    useEffect(() => {
        // Close edit modal when switching expanded jobs
        setEditProductNameModalOpen(false);
        setProductNameDraft('');
        setProductNameSaving(false);
    }, [expandedJobId]);

    // Load approved public library images for Explore tab (dynamic mode)
    useEffect(() => {
        if (!USE_DYNAMIC_EXPLORE) return;

        const fetchExploreImages = async () => {
            try {
                setExploreLoading(true);
                const payload = {
                    action: "get_ai_image_public_library",
                    status: "approved",
                };
                const res = await axios.post(ENVIRONMENT.AI_MODEL_HELPER, payload);
                const data = res.data;
                const rows = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
                const baseURL = ENVIRONMENT.getBaseURL();

                const mapped = rows.map((row) => {
                    const uri = row.uri || "";
                    const fullUrl = uri
                        ? (uri.startsWith("http://") || uri.startsWith("https://")
                            ? uri
                            : baseURL + uri)
                        : "";

                    return {
                        // Gallery expects src; for ai_explore we derive thumbnail/_512 in InfiniteScrollGallery
                        src: fullUrl,
                        caption: row.display_name,
                        display_name: row.display_name,
                        id: row.id,
                        render_id: row.render_id,
                        image_type: "ai_public",
                    };
                });

                setExploreImages(mapped);
            } catch (e) {
                console.error("Failed to load AI public library images for Explore tab", e);
            } finally {
                setExploreLoading(false);
            }
        };

        fetchExploreImages();
    }, []);

    useEffect(() => {
        // When expanding a job (or loading the page with expanded_job_id), scroll user to top of this section.
        if (!expandedJobId) return;
        setTimeout(() => {
            if (sectionTopRef.current && sectionTopRef.current.scrollIntoView) {
                sectionTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }, 0);
    }, [expandedJobId]);

    const handleExpandJob = (job_id) => {
        if (props.setActiveTabKey) props.setActiveTabKey('activity');
        if (props.setExpandedJobId) props.setExpandedJobId(String(job_id));
    };

    const handleCollapseJob = () => {
        if (props.setExpandedJobId) props.setExpandedJobId("");
    };

    const openEditProductNameModal = () => {
        if (!expandedItem) return;
        setProductNameDraft(expandedProductName);
        setEditProductNameModalOpen(true);
    };

    const handleSaveProductName = async () => {
        try {
            if (!expandedItem?.product_id) return;
            const newName = String(productNameDraft || '').trim();
            if (!newName) {
                message.error('Please enter a product name.');
                return;
            }
            setProductNameSaving(true);
            const payload = {
                product_id: expandedItem.product_id,
                product_name: newName,
                username: CUSTOMER_USERNAME,
            };
            await axios.post(ENVIRONMENT.UPDATE_PRODUCT, payload);
            message.success('Product name updated.');

            // Optimistic local update for immediate UI feedback
            if (props.setYourActivityRenders) {
                props.setYourActivityRenders((prev) => {
                    const arr = Array.isArray(prev) ? prev : [];
                    return arr.map((it) => {
                        if (String(it?.product_id ?? '') !== String(expandedItem.product_id)) return it;
                        return { ...it, product_name: newName };
                    });
                });
            }

            // Refresh from backend to keep everything consistent
            if (props.fetchYourActivityRendersData) {
                props.fetchYourActivityRendersData(null, false, true);
            }

            setEditProductNameModalOpen(false);
        } catch (e) {
            message.error('Failed to update product name.');
        } finally {
            setProductNameSaving(false);
        }
    };

    const getFilteredExpandedRenders = (item, filterKey) => {
        const renders = (item?.renders || []).filter(r => r && r.uri);
        const isVideo = (uri) => {
            const u = String(uri || '').toLowerCase();
            return u.endsWith('.mp4') || u.endsWith('.mov') || u.includes('.mp4') || u.includes('.mov');
        };

        if (filterKey === 'all') return renders;
        if (filterKey === 'video') return renders.filter(r => isVideo(r.uri));
        if (filterKey === 'edits') return renders.filter(r => r.request_type === 'edit_request');
        if (filterKey === 'generations') return renders.filter(r => r.request_type === 'more_generations' || r.request_type === 'new_request');
        if (filterKey === 'final') {
            return renders.filter(r => r.request_type === 'upscale_request');
        }
        return renders;
    };

    const getExpandedFilterLabel = (key) => {
        if (key === 'all') return 'All';
        if (key === 'generations') return 'Generations';
        if (key === 'edits') return 'Edits';
        if (key === 'final') return 'Final Images';
        if (key === 'video') return 'Video';
        return 'All';
    };

    const getExpandedCountForKey = (item, key) => {
        if (!item) return 0;
        return getFilteredExpandedRenders(item, key).length;
    };

    const getExpandedLoaderCount = (item, filterKey) => {
        if (!item) return 0;
        // Only show generation placeholders for filters where it makes sense.
        if (!(filterKey === 'all' || filterKey === 'generations')) return 0;

        const latestJobStatus = item.latest_status ?? item.status;
        if (latestJobStatus !== 'inprogress') return 0;

        const latestRequestId = item.latest_job_id ?? item.job_id ?? null;
        if (latestRequestId == null) return 0;
        const latestRequestIdStr = String(latestRequestId);

        const renders = item.renders || [];
        // Count only non-video image renders for the latest batch (request_id matches latest_job_id).
        const latestBatchRenderCount = renders.filter(r =>
            r &&
            r.uri &&
            String(r.request_id) === latestRequestIdStr &&
            r.asset_type !== 'ai_video'
        ).length;

        return Math.max(0, 5 - latestBatchRenderCount);
    };

    const redirectToGenerateMore = (item) => {
        // Compress large state (up to 5 image URIs + prompt) to avoid URL length limits.
        const toBase64Url = (uint8) => {
            let binary = '';
            for (let i = 0; i < uint8.length; i++) binary += String.fromCharCode(uint8[i]);
            return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
        };
        const compressState = (obj) => {
            const json = JSON.stringify(obj || {});
            const deflated = pako.deflate(json);
            return toBase64Url(deflated);
        };

        const params = new URLSearchParams();
        params.set("stage", "lifestyle_generation");
        params.set("productId", item.product_id);
        params.set("job_id", item.job_id || item.id || "");
        params.set("aiModel", "true");
        const state = {
            img: item.input_image_uri || "",
            imgInspection: item.inspiration_image_uri || "",
            inspiration_id: item.inspiration_id || null,
            prompt: item.prompt_text || item.flux_prompt || "",
            // New: include full props objects so next page can send them in payload with dimensions
            props: item.props || [],
            aspect_ratio: item.latest_aspect_ratio || "1:1",
        };
        params.set("state", compressState(state));
        window.location.href = `/ai-asset-creator?${params.toString()}`;
    };


    const regenerateRequest = (jobId) => {
        setRegenerateRequestLoaderId(jobId);
        let payload = {
            action: "regenerate_job",
            request_id: jobId,
        }
        axios.post(ENVIRONMENT.AI_MODEL_HELPER, payload).then((res) => {
            console.log(res);
            props.fetchYourActivityRendersData(() => {
                setRegenerateRequestLoaderId(null);
            }, false, true); // Poll silently without showing loader
        })
    }

    const handleSubmitForReview = (renderIndex, item) => {
        message.loading('Submitting for review...');
        console.log('render',renderIndex,item);
        const render = item.renders[renderIndex];
        const loaderId = `${item.product_id}_${render.render_id}`;
        setSubmitForReviewLoaderId(loaderId);
        
        const payload = {
            action: "add_review_image",
            image_id: render.render_id,
            submitted_by: CUSTOMER_USERNAME,
            product_id: item.product_id
        };
        
        axios.post(ENVIRONMENT.REVIEW_IMAGE_CONTROLLER, payload)
            .then((res) => {
                message.success("Image submitted for review successfully!");
                setSubmitForReviewLoaderId(null);
                props.fetchYourActivityRendersData(() => {
                    setRemoveRequestLoaderId(null);
                }, false, true);
            })
            .catch((error) => {
                message.error("Error submitting image for review");
                console.error(error);
                setSubmitForReviewLoaderId(null);
            });
    }

    const removeRequest = (jobId) => {
        setRemoveRequestLoaderId(jobId);
        let payload = {
            action: "delete_ai_render_request",
            request_id: jobId,
        }
        
        axios.post(ENVIRONMENT.AI_MODEL_HELPER, payload).then((res) => {
            console.log(res);
            props.fetchYourActivityRendersData(() => {
                setRemoveRequestLoaderId(null);
            }, false, true); // Poll silently without showing loader
        })
    }


    const handleEditClick = (renderIndex, item) => {
        const render = item.renders[renderIndex]
        console.log("item", render)
        const imageUrl = ENVIRONMENT.getBaseURL() + encodeURIComponent(render.uri);
        const productId = item.product_id;
        let prompt_text = item?.flux_prompt || item?.prompt_text;
        
        window.location.href = `/edit-ai-image?imageUrl=${imageUrl}&imageId=${render.render_id}&productId=${productId}&promptText=${prompt_text}`;
    }

    const handleCollaborate = async (renderIndex, item) => {
        message.loading('Collaborating on image...');
        const render = item.renders[renderIndex]
        console.log('Collaborate on image:', render, item);
    
        let prompt_text = item?.flux_prompt || item?.prompt_text;
    
        const toBase64Url = (uint8) => {
            let binary = '';
            for (let i = 0; i < uint8.length; i++) binary += String.fromCharCode(uint8[i]);
            return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
        };
        const compressState = (obj) => {
            const json = JSON.stringify(obj || {});
            const deflated = pako.deflate(json);
            return toBase64Url(deflated);
        };
        const collaborateState = {
            img: item.input_image_uri || "",
            imgInspection: item.inspiration_image_uri || "",
            inspiration_id: item.inspiration_id || null,
            prompt: item.prompt_text || "",
            props: item.props || [],
        };
        const collaborateParams = new URLSearchParams();
        collaborateParams.set("ai_image", "true");
        collaborateParams.set("productId", item.product_id);
        if (item.job_id) collaborateParams.set("job_id", String(item.job_id));
        if (item.inspiration_id != null) collaborateParams.set("inspiration_id", String(item.inspiration_id));
        collaborateParams.set("state", compressState(collaborateState));

        if(render.individual_collab_id){
           collaborateParams.set("individual_collab", "true");
           window.location.href = `/collaborate-tool/${render.individual_collab_id}?${collaborateParams.toString()}`;
        }
        else{
          setCollaborateLoader(true);
        
          const userName = localStorage.getItem('username')
    
          const payload = {
            image_id: render.render_id,
            display_name: render.display_name || "Untitled",
            camera_name: render.uri,
            thumbnail: render.uri.replace(/(\.[^.]+)$/, "_512$1"),
            created_by: userName,
            last_modified_by: userName,
            render_path: render.uri,
            state:{}
          }

        console.log('payload',payload)

        const res = await axios.post(ENVIRONMENT.CREATE_COLLABORATE_ENTITY, payload)

        console.log('res', res)
        collaborateParams.set("individual_collab", "true");
        window.location.href = `/collaborate-tool/${res.data.body.collaboration_entity_id}?${collaborateParams.toString()}`;
        setCollaborateLoader(false);
        }
      };

    const handlePreviewClick = (renderIndex, item) => {
        const render = item.renders[renderIndex]
        const imageUrl = ENVIRONMENT.getBaseURL() + render['uri'];
        setSelectedImageData({
            ...item,
            selectedImageUrl: imageUrl,
            renderIndex: renderIndex,
            render_id: render['render_id'],
            request_type: render['request_type'],
            asset_type: render['asset_type'],
            isFavourite: render['isFavourite'],
            favouriteId: render['favouriteId'],
            display_name: render['display_name'],
            is_edit_request: render['request_type'] == 'edit_request',
            edit_number: render['edit_number'],
            final_number: render['final_number'],
            base_number: render['base_number'],
            image_prompt: render['image_prompt'],
            video_prompt: render['video_prompt'],
            video_final_image_uri: render['video_final_image_uri'],
            render_submitted_on: render['render_submitted_on'],
            original_input_image_uri: render['original_input_image_uri'],
            props_image_uris: render['render_props_uris'] ? render['render_props_uris'] : getPropImageUris(item).map((prop) => prop.image_uri),
            default_props_image_uris: getPropImageUris(item).map((prop) => prop.image_uri)
        });
        setIsModalOpen(true);
        setCompareSiloModalOpen(false);
    }

    const handleCompareClick = (renderIndex, item) => {
        const render = item.renders[renderIndex];
        const imageUrl = ENVIRONMENT.getBaseURL() + render['uri'];
        setSelectedImageData({
            ...item,
            selectedImageUrl: imageUrl,
            renderIndex: renderIndex,
            render_id: render['render_id'],
            request_type: render['request_type'],
            isFavourite: render['isFavourite'],
            favouriteId: render['favouriteId'],
            display_name: render['display_name'],
            is_edit_request: render['request_type'] == 'edit_request',
            edit_number: render['edit_number'],
            final_number: render['final_number'],
            base_number: render['base_number'],
            image_prompt: render['image_prompt'],
            video_prompt: render['video_prompt'],
            render_submitted_on: render['render_submitted_on'],
            original_input_image_uri: render['original_input_image_uri'],
            props_image_uris: render['render_props_uris'] ? render['render_props_uris'] : getPropImageUris(item).map((prop) => prop.image_uri),
            default_props_image_uris: getPropImageUris(item).map((prop) => prop.image_uri)
        });
        setCompareSiloModalOpen(true);
    }

    const closeCompareModal = () => {
        setCompareSiloModalOpen(false);
        if (!isModalOpen) {
            setSelectedImageData(null);
        }
    }

    const handleGenerateVideoClick = (renderObj, e) => {
        if (e) e.stopPropagation();
        setSelectedVideoData(renderObj);
        setGenerateVideoModalOpen(true);
    };

    const handleVideoGenerationSuccess = () => {
        // Start forced polling for 2 minutes to catch the new in-progress asset
        const pollingDuration = 2 * 60 * 1000; // 2 minutes
        const pollingInterval = 10000; // 10 seconds
        const startTime = Date.now();

        const poll = () => {
            if (Date.now() - startTime < pollingDuration) {
                if (props.fetchYourActivityRendersData) {
                    props.fetchYourActivityRendersData(null, false, true);
                }
                setTimeout(poll, pollingInterval);
            }
        };

        // Initial trigger after 3 seconds to give backend time to create the record
        setTimeout(poll, 3000);
    };

    // useEffect(() => {
    //     console.log("selectedImageData", selectedImageData);
    // }, [selectedImageData])

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedImageData(null);
        setCompareSiloModalOpen(false);
    }


    useEffect(() => {
        // Only switch to explore tab if there are no activity images AND no in-progress bulk requests
        const hasInProgressBulkRequests = props.bulkRequests?.some(req => req.status === 'inprogress');
        if(props.activity_images?.length == 0 && !props.yourActivityLoading && !hasInProgressBulkRequests){
            props.setActiveTabKey('ai-created-images')
        }

    }, [props.activity_images, props.yourActivityLoading, props.bulkRequests])

    // Set edit_number / final_number on all render objects
    useEffect(() => {
        if (props.activity_images && props.activity_images.length > 0) {
            props.activity_images.forEach((item) => {
                if (item.renders && item.renders.length > 0) {
                    const renders = item.renders;
                    const totalEditRequests = renders.filter(r => r && r.request_type == 'edit_request').length;
                    const totalUpscaleRequests = renders.filter(r => r && r.request_type == 'upscale_request').length;
                    const totalBaseRequests = renders.filter(r => r && (r.request_type == 'new_request' || r.request_type == 'more_generations')).length;
                    
                    renders.forEach((renderObj, renderIndex) => {
                        if (renderObj && renderObj.uri) {
                            const editNumber = renderObj.request_type == 'edit_request' 
                                ? totalEditRequests - renders.slice(0, renderIndex).filter(r => r && r.request_type == 'edit_request').length
                                : null;
                            renderObj.edit_number = editNumber;

                            const finalNumber = renderObj.request_type == 'upscale_request'
                                ? totalUpscaleRequests - renders.slice(0, renderIndex).filter(r => r && r.request_type == 'upscale_request').length
                                : null;
                            renderObj.final_number = finalNumber;

                            const baseNumber = (renderObj.request_type == 'new_request' || renderObj.request_type == 'more_generations')
                                ? totalBaseRequests - renders.slice(0, renderIndex).filter(r => r && (r.request_type == 'new_request' || r.request_type == 'more_generations')).length
                                : null;
                            renderObj.base_number = baseNumber;

                            const totalVideoRequests = renders.filter(r => r && r.asset_type === 'ai_video').length;
                            const videoNumber = renderObj.asset_type == 'ai_video'
                                ? totalVideoRequests - renders.slice(0, renderIndex).filter(r => r && r.asset_type === 'ai_video').length
                                : null;
                            renderObj.video_number = videoNumber;
                        }
                    });
                }
            });
        }
    }, [props.activity_images])

    // Intersection Observer for infinite scroll (disabled when searching)
    useEffect(() => {
        if (!lastItemRef.current || !props.handleLoadMore) return;
        
        // Don't trigger infinite scroll when actively searching
        if (props.searchQuery && props.searchQuery.trim() !== '') return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !props.isLoadingMoreData && !props.allDataLoaded) {
                    props.handleLoadMore();
                }
            },
            { threshold: 0.1 }
        );

        observer.observe(lastItemRef.current);

        return () => {
            if (lastItemRef.current) {
                observer.unobserve(lastItemRef.current);
            }
        };
    }, [props.activity_images, props.isLoadingMoreData, props.allDataLoaded, props.handleLoadMore, props.searchQuery]);

    useEffect(() => {
        const handleResize = () => {
            Object.keys(rowRefs.current).forEach((key) => {
                updateScrollPosition(key);
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Row virtualization - IntersectionObserver to track visible rows
    const rowVisibilityObserver = useRef(null);
    
    useEffect(() => {
        // Create a single observer for all rows
        rowVisibilityObserver.current = new IntersectionObserver(
            (entries) => {
                setVisibleRows(prev => {
                    const newSet = new Set(prev);
                    entries.forEach(entry => {
                        const rowIndex = parseInt(entry.target.dataset.activityRow, 10);
                        if (!isNaN(rowIndex)) {
                            if (entry.isIntersecting) {
                                newSet.add(rowIndex);
                            } else {
                                newSet.delete(rowIndex);
                            }
                        }
                    });
                    return newSet;
                });
            },
            {
                rootMargin: '200px 0px', // 200px buffer above and below viewport
                threshold: 0
            }
        );

        return () => {
            if (rowVisibilityObserver.current) {
                rowVisibilityObserver.current.disconnect();
            }
        };
    }, []);

    // Helper to check if a row should render full content
    const shouldRenderRow = useCallback((rowIndex) => {
        if (visibleRows.size === 0) {
            // Initially render first few rows before observer kicks in
            return rowIndex < 5;
        }
        
        // Check if row is within buffer range of any visible row
        const visibleArray = Array.from(visibleRows);
        const minVisible = Math.min(...visibleArray);
        const maxVisible = Math.max(...visibleArray);
        
        return rowIndex >= minVisible - ROW_BUFFER && rowIndex <= maxVisible + ROW_BUFFER;
    }, [visibleRows, ROW_BUFFER]);

    // Callback ref to attach observer to each row
    const setRowRef = useCallback((element, index) => {
        if (element && rowVisibilityObserver.current) {
            rowVisibilityObserver.current.observe(element);
            rowObserverRefs.current[index] = element;
        }
    }, []);

    useEffect(() => {
        // Recalculate for all rows when activity_images updates
        const timeoutId = setTimeout(() => {
             Object.keys(rowRefs.current).forEach((key) => {
                updateScrollPosition(key);
            });
        }, 50);
        return () => clearTimeout(timeoutId);
    }, [props.activity_images]);

    const getProgress = (submittedOn) => {
        const elapsedTime = Date.now() - new Date(submittedOn + "Z").getTime()
        const totalDuration = 10 * 60 * 1000; // 10 minutes in milliseconds
        
        const progress = Math.min((elapsedTime / totalDuration) * 100, 99);
        return Math.max(0, Math.round(progress));
    };

    const markAiImageFavorite = (obj) => {
        const isVideo = obj?.asset_type === 'ai_video' || obj?.image_type === 'ai_video';
        const payload = {
          action: "add_favorite",
          entity_id: obj.render_id,
          entity_type: 'product',
          image_type: isVideo ? 'ai_video' : 'ai',
          favorited_by: CUSTOMER_USERNAME,
          image_name: isVideo ? 'Untitled' : obj?.render_id.toString() + ".jpg", 
        };
 
        axios.post(ENVIRONMENT.FAVOURITES_CONTROLLER, payload).then((res) => {
          props.fetchYourActivityRendersData(null, false, true); // Poll silently without showing loader
        });
      };
    
    const unmarkAiImageFavorite = (obj) => {
        const payload = {
          action: "remove_favorite",
          favorite_id: obj?.favouriteId,
          favorited_by: CUSTOMER_USERNAME,
        };
        axios.post(ENVIRONMENT.FAVOURITES_CONTROLLER, payload).then((res) => {
          props.fetchYourActivityRendersData(null, false, true); // Poll silently without showing loader
        });
      };

    const updatedRenderNameInImagesList = (imageData, updatedDisplayName) => {
        console.log("imageData", imageData);
        // Update the selected image data if it matches
        if (selectedImageData && selectedImageData.render_id === imageData.render_id) {
            setSelectedImageData(prev => ({
                ...prev,
                display_name: updatedDisplayName
            }));
        }
        
        // Refresh the activity data to get updated images
        props.fetchYourActivityRendersData(null, false, true); // Poll silently without showing loader
    };


    const goToNextImage = () => {
        if (!selectedImageData || !selectedImageData.renders) return;
        setImageDisplayLoader(true);
        const renders = selectedImageData.renders;
        const currentIndex = selectedImageData.renderIndex || 0;

        const nextIndex = (currentIndex + 1) >= renders.length ? 0 : currentIndex + 1;
        const renderDetails = renders[nextIndex];

        if (renderDetails) {
            const imageUrl = ENVIRONMENT.getBaseURL() + renderDetails.uri;
            
            setSelectedImageData({
                ...selectedImageData,
                selectedImageUrl: imageUrl,
                renderIndex: nextIndex,
                render_id: renderDetails['render_id'],
                request_type: renderDetails['request_type'],
                asset_type: renderDetails['asset_type'],
                resolution: renderDetails['resolution'],
                isFavourite: renderDetails['isFavourite'],
                favouriteId: renderDetails['favouriteId'],
                display_name: renderDetails['display_name'],
                is_edit_request: renderDetails['request_type'] == 'edit_request',
                edit_number: renderDetails['edit_number'],
                final_number: renderDetails['final_number'],
                base_number: renderDetails['base_number'],
                image_prompt: renderDetails['image_prompt'],
                render_submitted_on: renderDetails['render_submitted_on'],
                original_input_image_uri: renderDetails['original_input_image_uri'],
                video_prompt: renderDetails['video_prompt'],
                props_image_uris: renderDetails['render_props_uris'] ? renderDetails['render_props_uris'] : selectedImageData.default_props_image_uris
            });

            if (renderDetails.asset_type !== 'ai_video') {
                const img = new window.Image();
                img.onload = () => {
                    setImageDisplayLoader(false);
                }
                img.src = imageUrl;
            } else {
                setImageDisplayLoader(false);
            }
        } else {
             // No valid next image found (possibly all videos or empty?)
             setImageDisplayLoader(false);
        }
    };

    const goToPreviousImage = () => {
        if (!selectedImageData || !selectedImageData.renders) return;
        setImageDisplayLoader(true);
        const renders = selectedImageData.renders;
        const currentIndex = selectedImageData.renderIndex || 0;

        const prevIndex = (currentIndex - 1) < 0 ? renders.length - 1 : currentIndex - 1;
        const renderDetails = renders[prevIndex];

        if (renderDetails) {
            const imageUrl = ENVIRONMENT.getBaseURL() + renderDetails.uri;
            
            setSelectedImageData({
                ...selectedImageData,
                selectedImageUrl: imageUrl,
                renderIndex: prevIndex,
                render_id: renderDetails['render_id'],
                request_type: renderDetails['request_type'],
                asset_type: renderDetails['asset_type'],
                resolution: renderDetails['resolution'],
                isFavourite: renderDetails['isFavourite'],
                favouriteId: renderDetails['favouriteId'],
                display_name: renderDetails['display_name'],
                is_edit_request: renderDetails['request_type'] == 'edit_request',
                edit_number: renderDetails['edit_number'],
                final_number: renderDetails['final_number'],
                base_number: renderDetails['base_number'],
                image_prompt: renderDetails['image_prompt'],
                render_submitted_on: renderDetails['render_submitted_on'],
                original_input_image_uri: renderDetails['original_input_image_uri'],
                video_prompt: renderDetails['video_prompt'],
                props_image_uris: renderDetails['render_props_uris'] ? renderDetails['render_props_uris'] : selectedImageData.default_props_image_uris
            });

            if (renderDetails.asset_type !== 'ai_video') {
                const img = new window.Image();
                img.onload = () => {
                    setImageDisplayLoader(false);
                }
                img.src = imageUrl;
            } else {
                setImageDisplayLoader(false);
            }
        } else {
            setImageDisplayLoader(false);
        }
    };

    const deleteImage = () => {
        setDeleteLoader(true);
        let payload = {
            action: (deleteModal.asset_type === 'ai_video' || deleteModal.image_type === 'ai_video') ? "delete_ai_video" : "delete_ai_image",
            id: deleteModal.render_id,
          }
        
        axios.post(ENVIRONMENT.AI_MODEL_HELPER, payload)
          .then((response) => {
            message.info("Image Deleted.");
          })
          .catch((error) => {
            message.error("Error in deleting Image");
          })
          .finally(() => {
            handleModalClose();
            setDeleteModal(undefined);
            setDeleteLoader(false);

            // Remove the render with matching render_id from activity_images
            let requests = props.activity_images.map(request => ({
                ...request,
                renders: request.renders.filter(render => render.render_id !== deleteModal.render_id)
            }));
            props.setYourActivityRenders(requests);
            
        });
        
    }

    const handleDeleteClick = (image) =>{
        setDeleteModal(image)
    }

    // Scroll functions for the image rows
    const handleScrollLeft = (rowIndex) => {
        const container = rowRefs.current[rowIndex];
        if (container) {
            const scrollAmount = container.offsetWidth;
            container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
        }
    };

    const handleScrollRight = (rowIndex, totalRenders = null) => {
        const container = rowRefs.current[rowIndex];
        if (container) {
            const scrollAmount = container.offsetWidth;
            container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
            
            // Check if we're scrolling near the end and should load more images
            if (totalRenders !== null) {
                const nearEnd = container.scrollLeft + container.offsetWidth * 2 >= container.scrollWidth;
                if (nearEnd) {
                    loadMoreRenders(rowIndex, totalRenders);
                }
            }
        }
    };

    const updateScrollPosition = (rowIndex, totalRenders = null) => {
        const container = rowRefs.current[rowIndex];
        if (container) {
            setScrollPositions(prev => ({
                ...prev,
                [rowIndex]: {
                    scrollLeft: container.scrollLeft,
                    maxScroll: container.scrollWidth - container.clientWidth
                }
            }));
            
            // Load more images when scrolling near the end
            if (totalRenders !== null) {
                const nearEnd = container.scrollLeft + container.offsetWidth * 1.5 >= container.scrollWidth;
                if (nearEnd) {
                    loadMoreRenders(rowIndex, totalRenders);
                }
            }
        }
    };

    const canScrollLeft = (rowIndex) => {
        const position = scrollPositions[rowIndex];
        // Default to false if no position data (assume at start)
        if (!position) return false;
        return position.scrollLeft > 0;
    };

    const canScrollRight = (rowIndex) => {
        const position = scrollPositions[rowIndex];
        // Default to true if no position data (assume we can scroll right if buttons are rendered)
        if (!position) return true;
        return position.scrollLeft < position.maxScroll - 1;
    };

    const isScrollable = (rowIndex) => {
        const position = scrollPositions[rowIndex];
        return position && position.maxScroll > 1;
    };

    const renderDelete = (x) => {
        return (
          <Tooltip title={<span className="manrope f-12 white">Delete</span>}>
            <Button
              onClick={() => handleDeleteClick(x)}
              className="manrope f-14 w-500 lh-22 ai-action-btn ai-action-btn-red"
              icon={<DeleteOutlined />}
            />
          </Tooltip>
        );
      };

    // Helper function to build all image URLs for an item (silo + inspiration + props)
    const getAllImageUrls = (item) => {
        const images = [];
        const baseURL = item.platform ? ENVIRONMENT.getBaseURL(item.platform) : ENVIRONMENT.getBaseURL();
        
        // Add silo (product image) first
        if (item.input_image_uri) {
            images.push({
                src: item.input_image_uri.includes('https://') 
                    ? item.input_image_uri 
                    : baseURL + item.input_image_uri,
                alt: 'Product Image'
            });
        }
        
        // Add inspiration if exists
        if (item.inspiration_image_uri) {
            images.push({
                src: item.inspiration_image_uri.includes('https://') 
                    ? item.inspiration_image_uri 
                    : baseURL + item.inspiration_image_uri,
                alt: 'Inspiration Image'
            });
        }
        
        // Add all props
        const propsData = getPropImageUris(item) || [];
        propsData.forEach((prop, idx) => {
            const propUri = prop.image_uri;
            images.push({
                src: propUri.includes('https://') 
                    ? propUri 
                    : baseURL + propUri,
                alt: `Prop Image ${idx + 1}`
            });
        });
        
        return images;
    };

    // Handle click on placeholder to open preview
    const handlePlaceholderClick = (item, itemIndex) => {
        const propsUris = getPropImageUris(item) || [];
        const hasInspiration = !!item.inspiration_image_uri;
        const propsCount = propsUris.length;
        
        // Determine which prop image to open based on the case
        let propRefKey = null;
        
        if (propsCount === 1 && hasInspiration) {
            // Case 3: One prop + inspiration - first prop is hidden
            propRefKey = `${itemIndex}-prop-0`;
        } else if (propsCount > 1 && !hasInspiration) {
            // Case 4: Multiple props, no inspiration - first prop is visible, second prop is hidden
            propRefKey = `${itemIndex}-prop-1`;
        } else if (propsCount > 1 && hasInspiration) {
            // Case 5: Multiple props + inspiration - first prop is hidden
            propRefKey = `${itemIndex}-prop-0`;
        }
        
        // Try to use the ref to find and click the first prop image
        if (propRefKey) {
            const propRef = previewImageRefs.current[propRefKey];
            if (propRef) {
                const imgElement = propRef.querySelector('img');
                if (imgElement) {
                    imgElement.click();
                    return;
                }
            }
        }
        
        // Fallback: find the first hidden prop image in the DOM
        const rowElement = document.querySelector(`[data-activity-row="${itemIndex}"]`);
        if (rowElement) {
            // Find hidden images (props that aren't displayed)
            const hiddenImages = rowElement.querySelectorAll('.ant-image[style*="display: none"]');
            if (hiddenImages.length > 0) {
                const firstHiddenImg = hiddenImages[0].querySelector('img');
                if (firstHiddenImg) {
                    firstHiddenImg.click();
                    return;
                }
            }
            
            // Last resort: click the third image in the group (should be first prop)
            const allImageWrappers = rowElement.querySelectorAll('.ant-image');
            if (allImageWrappers.length >= 3) {
                const thirdImage = allImageWrappers[2];
                const imgElement = thirdImage.querySelector('img');
                if (imgElement) {
                    imgElement.click();
                }
            }
        }
    };

    return (
        <>
        <Row ref={sectionTopRef} className="container-fluid ai-content-container justify-space-around align-flex-start mt-40 image-grid-padding w-100">
            <Col span={24} className="img-grid-container">
                <Tabs
                    destroyInactiveTabPane={false}
                    className='items-table'
                    tabBarGutter={32}
                    size="large"
                    onChange={(key) => props.setActiveTabKey(key)}
                    activeKey={resolvedActiveTabKey}
                    tabBarExtraContent={
                        resolvedActiveTabKey === 'activity' && !expandedJobId ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '16px' }}>
                                <Input
                                    placeholder="Search"
                                    prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                    suffix={
                                        props.searchQuery ? (
                                            <CloseCircleOutlined 
                                                onClick={handleClearSearch}
                                                style={{ color: '#bfbfbf', cursor: 'pointer' }}
                                            />
                                        ) : null
                                    }
                                    value={props.searchQuery}
                                    onChange={handleSearchChange}
                                    style={{ width: 350 }}
                                    className="manrope f-14"
                                    allowClear={false}
                                />
                                {props.isSearchFetchingAll && (
                                    <Tooltip title={<span className="manrope f-12 white">Searching all items...</span>}>
                                        <LoadingOutlined style={{ color: '#276DD7', fontSize: '16px' }} />
                                    </Tooltip>
                                )}
                            </div>
                        ) : null
                    }
                >
                    {!(ENVIRONMENT.COMPANY_IDS_WITH_CUSTOM_AI_OUTPUTS.includes(localStorage.getItem("company_id")) || (props.activity_images?.length == 0 && !props.yourActivityLoading && !props.bulkRequests?.some(r => r.status === 'inprogress'))) && (
                        <TabPane
                        tab={
                            <span
                                className={`manrope f-14 w-700 asset-tab-heading`}
                                onMouseEnter={() => setHoveredTabKey('activity')}
                                onMouseLeave={() => setHoveredTabKey(null)}
                                style={{ color: resolvedActiveTabKey === 'activity' ? '#276DD7' : '#000000' }}
                            >
                                <span
                                    style={{ marginRight: '10px' }}
                                    dangerouslySetInnerHTML={{ __html: getGridIcon((resolvedActiveTabKey === 'activity' || hoveredTabKey === 'activity') ? '#276DD7' : '#000000') }}
                                />
                                 Your Activity
                            </span>
                        }
                        key={'activity'}>
                        {props.yourActivityLoading ?
                            <AiLoader /> :
                            <>
                            {!expandedJobId && props.bulkRequests && props.bulkRequests
                                .filter(r => 
                                    r.status === 'inprogress' || 
                                    ((r.status === 'completed') && props.recentlyCompletedBulkRequestIds?.has(r.id))
                                )
                                .map((req) => {
                                    const isComplete = req.status === 'completed';
                                    return (
                                <div className="mb-20" bodyStyle={{ padding: 0 }} key={req.id}>
                                    <Row gutter={24} style={{ marginBottom: 20 }}>
                                        {/* Left Column: File Icon & Name */}
                                        <Col flex="0 0 240px" style={{ textAlign: 'left'}}>
                                            <div style={{ marginBottom: 8 }}>
                                                <div style={{ 
                                                    width: 64, 
                                                    height: 72, 
                                                    border: '1px solid #D9D9D9', 
                                                    borderRadius: 8, 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'center',
                                                    fontSize: 24,
                                                    color: '#000',
                                                    backgroundColor: '#fff'
                                                }}>
                                                    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                                                        <FileTextOutlined style={{ fontSize: '24px', marginBottom: 4 }} />
                                                        <span className="manrope f-10 w-700" style={{lineHeight: 1}}>CSV</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="manrope f-14 w-500 black-00" style={{ wordBreak: 'break-word', lineHeight: '1.4', marginLeft: 3 }}>
                                                {req.bulk_upload_name.length > 50 
                                                    ?req.bulk_upload_name.substring(0, 42) + '...' +req.bulk_upload_name.substring(req.bulk_upload_name.length - 8) 
                                                :req.bulk_upload_name}
                                            </div>
                                        </Col>

                                        {/* Right Column: Blue Info Box */}
                                        <Col flex="auto" style={{marginRight: 30}}>
                                            <div style={{ 
                                                backgroundColor: '#F0F5FF', 
                                                borderRadius: 4, 
                                                padding: '40px 20px', 
                                                display: 'flex', 
                                                flexDirection: 'column', 
                                                alignItems: 'center', 
                                                justifyContent: 'center',
                                                height: '100%',
                                                textAlign: 'center'
                                            }}>
                                                <div className="manrope w-700 f-16" style={{ color: '#10239E', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    {isComplete ? (
                                                        <div className="bulk-job-complete-icon">
                                                            <img src="/img/ai_creator/bulk-job-check-mark.svg" alt="" />
                                                        </div>
                                                    ) : (
                                                        <img src="/img/ai_creator/ai-twinkle-blue.svg" alt="" style={{width: 16}} />
                                                    )}
                                                    {isComplete ? 'Your bulk upload is complete' : 'Your bulk upload is in progress'}
                                                </div>
                                                {!isComplete && (
                                                    <div className="manrope f-14" style={{ color: '#10239E' }}>
                                                        Image generations will appear below in your feed as they finish rendering.
                                                    </div>
                                                )}
                                            </div>
                                        </Col>
                                    </Row>

                                    {/* Bottom Row: Progress Bar - only show for in-progress */}
                                    {!isComplete && (() => {
                                        const completed = req.completed_requests_count || 0;
                                        const total = req.total_requests_count || 0;
                                        const progressPercent = total > 0 ? Math.round((completed / total) * 100) : 0;
                                        
                                        return (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginRight: 30, marginLeft: 10 }}>
                                                <span className="manrope f-14 w-500 black-00" style={{ whiteSpace: 'nowrap', minWidth: '100px' }}>
                                                    {completed} / {total} processed
                                                </span>
                                                <div style={{ flex: 1 }}>
                                                    <Progress 
                                                        percent={progressPercent} 
                                                        showInfo={false} 
                                                        strokeColor={{
                                                            "0%": "#7883DC",
                                                            "50%": "#276DD7",
                                                            "100%": "#91D5FF",
                                                        }} 
                                                        trailColor="#F0F0F0" 
                                                        strokeLinecap="square"
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })()}
                                    <Divider style={{ margin: '24px 0' }} />
                                </div>
                                    );
                                })}

                            {expandedJobId && (
                                <div className="ai-expanded-header">
                                    <div className="ai-expanded-header-left">
                                        {expandedItem && (
                                            <Breadcrumb separator="/" style={{ margin: 0 }}>
                                                <Breadcrumb.Item>
                                                    <span
                                                        className="manrope f-14 lh-28 black-73 white-space-text cursor-pointer"
                                                        style={{ display: 'inline-flex', alignItems: 'center' }}
                                                        onClick={handleCollapseJob}
                                                    >
                                                        <ArrowLeftOutlined className="mr-8" />
                                                        All Generations
                                                    </span>
                                                </Breadcrumb.Item>
                                                <Breadcrumb.Item>
                                                    <div style={{ display: 'inline-flex', alignItems: 'center', minWidth: 0 }}>
                                                        <Tooltip placement="bottomLeft" title={<span className="manrope f-12 white">{expandedProductName}</span>}>
                                                            <span
                                                                className="manrope f-16 w-600 black-00"
                                                                style={{
                                                                    maxWidth: 320,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    display: 'inline-block'
                                                                }}
                                                            >
                                                                {expandedProductName}
                                                            </span>
                                                        </Tooltip>
                                                        <Tooltip title={<span className="manrope f-12 white">Edit product name</span>}>
                                                            <EditOutlined
                                                                className="ml-8 manrope blue cursor-pointer"
                                                                onClick={openEditProductNameModal}
                                                            />
                                                        </Tooltip>
                                                    </div>
                                                </Breadcrumb.Item>
                                            </Breadcrumb>
                                        )}
                                    </div>
                                    <div className="ai-expanded-header-right">
                                        {expandedItem && (
                                            <>
                                                <Select
                                                    value={expandedFilterKey}
                                                    onChange={(val) => setExpandedFilterKey(val)}
                                                    className="ai-expanded-filter-select"
                                                    optionLabelProp="label"
                                                    dropdownMatchSelectWidth={false}
                                                >
                                                    <Option value="all" label={`All (${getExpandedCountForKey(expandedItem, 'all')})`}>
                                                        All ({getExpandedCountForKey(expandedItem, 'all')})
                                                    </Option>
                                                    <Option value="generations" label={`Generations (${getExpandedCountForKey(expandedItem, 'generations')})`}>
                                                        Generations ({getExpandedCountForKey(expandedItem, 'generations')})
                                                    </Option>
                                                    <Option value="edits" label={`Edits (${getExpandedCountForKey(expandedItem, 'edits')})`}>
                                                        Edits ({getExpandedCountForKey(expandedItem, 'edits')})
                                                    </Option>
                                                    <Option value="final" label={`Final Images (${getExpandedCountForKey(expandedItem, 'final')})`}>
                                                        Final Images ({getExpandedCountForKey(expandedItem, 'final')})
                                                    </Option>
                                                    <Option value="video" label={`Video (${getExpandedCountForKey(expandedItem, 'video')})`}>
                                                        Video ({getExpandedCountForKey(expandedItem, 'video')})
                                                    </Option>
                                                </Select>
                                                <Tooltip title={<span className="manrope f-12 white">Generate more</span>}>
                                                    <Button
                                                        className="icon-button-container ai-button mr-8"
                                                        onClick={() => redirectToGenerateMore(expandedItem)}
                                                    >
                                                        <RedoOutlined />
                                                    </Button>
                                                </Tooltip>
                                                <Tooltip title={<span className="manrope f-12 white">Product details</span>}>
                                                    <Button
                                                        className="icon-button-container ai-button mr-8"
                                                        onClick={() => window.location.href = ('/products/' + expandedItem.product_id)}
                                                    >
                                                        <UnorderedListOutlined />
                                                    </Button>
                                                </Tooltip>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            {expandedJobId && expandedItem ? (
                                <div className="ai-expanded-container">
                                    <div className="ai-expanded-output-grid">
                                        <Row gutter={[12, 12]}>
                                            {/* Loader placeholders for latest in-progress generation batch */}
                                            {[...Array(getExpandedLoaderCount(expandedItem, expandedFilterKey))].map((_, idx) => (
                                                <Col key={`expanded-loader-${idx}`} xs={12} sm={8} md={6}>
                                                    <div className="activity-image-card-wrapper">
                                                        <Card style={{ cursor: 'auto' }} className="your-activity-card border loading">
                                                            <LoadingOutlined />
                                                        </Card>
                                                    </div>
                                                </Col>
                                            ))}
                                            {getFilteredExpandedRenders(expandedItem, expandedFilterKey)
                                                .map((renderObj, renderIndex) => ({ renderObj, renderIndex }))
                                                .map(({ renderObj, renderIndex }) => {
                                                    const engineVal = renderObj?.engine || expandedItem.engine;
                                                    const engineDisplayName = getDisplayEngineName(engineVal);
                                                    const engineClass = getEngineCssClass(engineVal);
                                                    const engineLogo = getEngineLogo(engineVal);

                                                    const orientation = getImageOrientation(renderObj.width, renderObj.height);
                                                    const colProps = orientation === 'landscape' 
                                                        ? { xs: 24, sm: 12, md: 10 } 
                                                        : orientation === 'portrait'
                                                            ? { xs: 8, sm: 6, md: 4 }
                                                            : { xs: 12, sm: 8, md: 6 };

                                                    return (
                                                        <Col key={renderObj.render_id || renderIndex} {...colProps}>
                                                            <div className={`activity-image-card-wrapper ${orientation}`} key={renderIndex}>
                                                                {renderObj.asset_type === 'ai_video' ? (
                                                                    <Card className={`your-activity-card ${engineClass}`} onClick={() => {
                                                                        handlePreviewClick(renderIndex, expandedItem);
                                                                    }}>
                                                                        <div className="activity-video-thumbnail-container" style={{ position: 'relative', width: '100%', height: '100%' }}>
                                                                            <CustomImage
                                                                                src={ENVIRONMENT.getBaseURL() + renderObj.thumbnail} 
                                                                                preview={false}
                                                                                className={`your-activity-img ${engineClass}`}
                                                                            />
                                                                            <div className="ai-video-play-overlay">
                                                                                <img src={playIconSvg} alt="Play" className="ai-video-play-icon" />
                                                                            </div>
                                                                            {renderObj.video_number && (
                                                                                <div className="engine-logo-badge">
                                                                                    <span className="engine-name">Video {renderObj.video_number}</span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </Card>
                                                                ) : (
                                                                    <Card className={`your-activity-card ${engineClass}`} onClick={() => {
                                                                        const render = expandedItem.renders[renderIndex]
                                                                        if(render.review_status == 'rejected') {
                                                                            let prompt_text = expandedItem?.prompt_text;
                                                                                    const toBase64Url = (uint8) => {
                                                                                        let binary = '';
                                                                                        for (let i = 0; i < uint8.length; i++) binary += String.fromCharCode(uint8[i]);
                                                                                        return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
                                                                                    };
                                                                                    const compressState = (obj) => {
                                                                                        const json = JSON.stringify(obj || {});
                                                                                        const deflated = pako.deflate(json);
                                                                                        return toBase64Url(deflated);
                                                                                    };
                                                                                    const collaborateState = {
                                                                                        img: expandedItem.input_image_uri || "",
                                                                                        imgInspection: expandedItem.inspiration_image_uri || "",
                                                                                        inspiration_id: expandedItem.inspiration_id || null,
                                                                                        prompt: expandedItem.prompt_text || "",
                                                                                        props: expandedItem.props || [],
                                                                                    };
                                                                                    const collaborateParams = new URLSearchParams();
                                                                                    collaborateParams.set("ai_image", "true");
                                                                                    collaborateParams.set("productId", expandedItem.product_id);
                                                                                    if (expandedItem.job_id) collaborateParams.set("job_id", String(expandedItem.job_id));
                                                                                    if (expandedItem.inspiration_id != null) collaborateParams.set("inspiration_id", String(expandedItem.inspiration_id));
                                                                                    collaborateParams.set("state", compressState(collaborateState));
                                                                                    window.location.href = `/collaborate-tool/${render.collab_id}?${collaborateParams.toString()}`;
                                                                        }
                                                                        else {
                                                                            handlePreviewClick(renderIndex, expandedItem);
                                                                        }
                                                                    }}>
                                                                    <CustomImage
                                                                        src={ENVIRONMENT.getBaseURL() + renderObj.uri.replace(/(\.[^.]+)$/, "_512$1")}
                                                                        preview={false}
                                                                        className={`your-activity-img ${engineClass}`}
                                                                    />
                                                                    {renderObj.review_status == 'rejected' ? (
                                                                        <div className="engine-logo-badge red">
                                                                            <span className="engine-name">Rejected</span>
                                                                        </div>
                                                                    ) : renderObj.review_status == 'approved' ? (
                                                                        <div className="engine-logo-badge green">
                                                                            <span className="engine-name">Approved</span>
                                                                        </div>
                                                                    ) : renderObj.review_status == 'pending' ? (
                                                                        <div className="engine-logo-badge blue">
                                                                            <span className="engine-name">In Review</span>
                                                                        </div>
                                                                    ) : renderObj.request_type == 'edit_request' ? (
                                                                        <div className="engine-logo-badge">
                                                                            <span className="engine-name">Edit {renderObj.edit_number}</span>
                                                                        </div>
                                                                    ) : renderObj.request_type == 'upscale_request' ? (
                                                                        <div className="engine-logo-badge">
                                                                            <span className="engine-name">Final {renderObj.final_number}</span>
                                                                        </div>
                                                                    ) : (renderObj.request_type == 'new_request' || renderObj.request_type == 'more_generations') ? (
                                                                        <div className="engine-logo-badge">
                                                                            <span className="engine-name">Base {renderObj.base_number}</span>
                                                                        </div>
                                                                    ) : engineVal?.toLowerCase() === 'qwen' ? (
                                                                        <div className="engine-logo-badge proprietary-badge">
                                                                            <img src={engineLogo} alt={engineDisplayName} />
                                                                            <span className="engine-name">Poppy</span>
                                                                        </div>
                                                                    ) : engineLogo && (
                                                                        <div className="engine-logo-badge">
                                                                            <img src={engineLogo} alt={engineDisplayName} />
                                                                            <span className="engine-name">{engineDisplayName}</span>
                                                                        </div>
                                                                    )}
                                                                    {/* Portrait layout: left buttons stay at top, right buttons split into rows */}
                                                                    {orientation === 'portrait' ? (
                                                                        <>
                                                                            <div className={`image-action-buttons ${expandedItem.renders[renderIndex].individual_annotation_count > 0 || expandedItem.renders[renderIndex].individual_collab_id ? 'always-visible': ''} left`}>
                                                                                <Tooltip title={
                                                                                    <>
                                                                                    <span className="manrope f-12 white">Collaborate</span>
                                                                                    <br />
                                                                                    {expandedItem.renders[renderIndex].individual_annotation_count > 0 
                                                                                    ? "Total Comments: " + expandedItem.renders[renderIndex].individual_annotation_count : ""}
                                                                                    </>
                                                                                }>
                                                                                    <Button 
                                                                                        className={`action-button edit-button ${expandedItem.renders[renderIndex].individual_annotation_count > 0 ? 'annotation': expandedItem.renders[renderIndex].individual_collab_id ? 'no-annotation' : ''}`}
                                                                                        type="text"
                                                                                        shape="circle"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleCollaborate(renderIndex, expandedItem);
                                                                                        }}
                                                                                    >
                                                                                        <div style={{display: 'flex', alignItems: 'center'}}>
                                                                                        {collaborateLoader ?
                                                                                            <LoadingOutlined style={{ color: '#fff', fontSize: '16px' }} /> :
                                                                                            <img src={expandedItem.renders[renderIndex].individual_annotation_count > 0 || expandedItem.renders[renderIndex].individual_collab_id ? "/img/collaborate_tool/new-new-collab.svg" : "/img/collaborate_tool/new-new-collab-white.svg"} style={{width: '18px', height: '13px'}} />
                                                                                        }
                                                                                        {       
                                                                                            expandedItem.renders[renderIndex].individual_annotation_count > 0 ?
                                                                                            <span className="manrope f-14 w-500" style={{marginLeft: 8}}>{expandedItem.renders[renderIndex].individual_annotation_count}</span>
                                                                                            : ""
                                                                                        }
                                                                                        </div>
                                                                                    </Button>
                                                                                </Tooltip>
                                                                                <Tooltip title={<span className="manrope f-12 white">Submit for review</span>}>
                                                                                    <Button
                                                                                        className="action-button review-button"
                                                                                        type="text"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleSubmitForReview(renderIndex, expandedItem);
                                                                                        }}
                                                                                    >
                                                                                        {submitForReviewLoaderId === `${expandedItem.product_id}_${expandedItem.renders[renderIndex].render_id}` ?
                                                                                            <LoadingOutlined style={{ color: '#fff', fontSize: '16px' }} /> :
                                                                                            <img src={'/img/ai_creator/submit_for_review_white.svg'}/>
                                                                                        }
                                                                                    </Button>
                                                                                </Tooltip>
                                                                            </div>
                                                                            {renderObj.request_type == 'upscale_request' ? (
                                                                                <>
                                                                                    <div className="image-action-buttons" style={{right: '8px', top: '8px'}}>
                                                                                        <Tooltip title={<span className="manrope f-12 white">Generate Video</span>}>
                                                                                            <Button
                                                                                                className="action-button preview-button"
                                                                                                type="text"
                                                                                                shape="circle"
                                                                                                onClick={(e) => handleGenerateVideoClick(renderObj, e)}
                                                                                            >
                                                                                                <img src="/img/white-video-camera-icon.svg" style={{width: '18px', height: '13px'}} alt="Video" />
                                                                                            </Button>
                                                                                        </Tooltip>
                                                                                        <Tooltip title={<span className="manrope f-12 white">Edit image</span>}>
                                                                                            <Button
                                                                                                className="action-button edit-button"
                                                                                                type="text"
                                                                                                shape="square"
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    handleEditClick(renderIndex, expandedItem);
                                                                                                }}
                                                                                            >
                                                                                                <EditOutlined />
                                                                                            </Button>
                                                                                        </Tooltip>
                                                                                    </div>
                                                                                    <div className="image-action-buttons" style={{right: '8px', top: '48px'}}>
                                                                                        <Tooltip title={<span className="manrope f-12 white">Compare with silo</span>}>
                                                                                            <Button
                                                                                                className="action-button compare-button"
                                                                                                type="text"
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    handleCompareClick(renderIndex, expandedItem);
                                                                                                }}
                                                                                            >
                                                                                                <img src={'/img/ai_creator/compare.svg'} />
                                                                                            </Button>
                                                                                        </Tooltip>
                                                                                    </div>
                                                                                </>
                                                                            ) : (
                                                                                <div className="image-action-buttons" style={{right: '8px', top: '8px'}}>
                                                                                    <Tooltip title={<span className="manrope f-12 white">Edit image</span>}>
                                                                                        <Button
                                                                                            className="action-button edit-button"
                                                                                            type="text"
                                                                                            shape="square"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                handleEditClick(renderIndex, expandedItem);
                                                                                            }}
                                                                                        >
                                                                                            <EditOutlined />
                                                                                        </Button>
                                                                                    </Tooltip>
                                                                                    <Tooltip title={<span className="manrope f-12 white">Compare with silo</span>}>
                                                                                        <Button
                                                                                            className="action-button compare-button"
                                                                                            type="text"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                handleCompareClick(renderIndex, expandedItem);
                                                                                            }}
                                                                                        >
                                                                                            <img src={'/img/ai_creator/compare.svg'} />
                                                                                        </Button>
                                                                                    </Tooltip>
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    ) : (
                                                                        /* Standard layout for landscape/square */
                                                                        <>
                                                                            <div className={`image-action-buttons ${expandedItem.renders[renderIndex].individual_annotation_count > 0 || expandedItem.renders[renderIndex].individual_collab_id ? 'always-visible': ''} left`}>
                                                                                <Tooltip title=
                                                                                {
                                                                                <>
                                                                                <span className="manrope f-12 white">Collaborate</span>
                                                                                <br />
                                                                                {expandedItem.renders[renderIndex].individual_annotation_count > 0 
                                                                                ? "Total Comments: " + expandedItem.renders[renderIndex].individual_annotation_count : ""}
                                                                                </>
                                                                                }>
                                                                                    <Button 
                                                                                        className={`action-button edit-button ${expandedItem.renders[renderIndex].individual_annotation_count > 0 ? 'annotation': expandedItem.renders[renderIndex].individual_collab_id ? 'no-annotation' : ''}`}
                                                                                        type="text"
                                                                                        shape="circle"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleCollaborate(renderIndex, expandedItem);
                                                                                        }}
                                                                                    >
                                                                                        <div style={{display: 'flex', alignItems: 'center'}}>
                                                                                        {collaborateLoader ?
                                                                                            <LoadingOutlined style={{ color: '#fff', fontSize: '16px' }} /> :
                                                                                            <img src={expandedItem.renders[renderIndex].individual_annotation_count > 0 || expandedItem.renders[renderIndex].individual_collab_id ? "/img/collaborate_tool/new-new-collab.svg" : "/img/collaborate_tool/new-new-collab-white.svg"} style={{width: '18px', height: '13px'}} />
                                                                                        }
                                                                                        {       
                                                                                            expandedItem.renders[renderIndex].individual_annotation_count > 0 ?
                                                                                            <span className="manrope f-14 w-500" style={{marginLeft: 8}}>{expandedItem.renders[renderIndex].individual_annotation_count}</span>
                                                                                            : ""
                                                                                        }
                                                                                        </div>
                                                                                    </Button>
                                                                                </Tooltip>
                                                                                <Tooltip title={<span className="manrope f-12 white">Submit for review</span>}>
                                                                                    <Button
                                                                                        className="action-button review-button"
                                                                                        type="text"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleSubmitForReview(renderIndex, expandedItem);
                                                                                        }}
                                                                                    >
                                                                                        {submitForReviewLoaderId === `${expandedItem.product_id}_${expandedItem.renders[renderIndex].render_id}` ?
                                                                                            <LoadingOutlined style={{ color: '#fff', fontSize: '16px' }} /> :
                                                                                            <img src={'/img/ai_creator/submit_for_review_white.svg'}/>
                                                                                        }
                                                                                    </Button>
                                                                                </Tooltip>
                                                                            </div>
                                                                            <div className="image-action-buttons right">
                                                                                {renderObj.request_type == 'upscale_request' ? (
                                                                                    <Tooltip title={<span className="manrope f-12 white">Generate Video</span>}>
                                                                                        <Button
                                                                                            className="action-button preview-button"
                                                                                            type="text"
                                                                                            shape="circle"
                                                                                            onClick={(e) => handleGenerateVideoClick(renderObj, e)}
                                                                                        >
                                                                                            <img src="/img/white-video-camera-icon.svg" style={{width: '18px', height: '13px'}} alt="Video" />
                                                                                        </Button>
                                                                                    </Tooltip>
                                                                                ) : ""}
                                                                                <Tooltip title={<span className="manrope f-12 white">Edit image</span>}>
                                                                                    <Button
                                                                                        className="action-button edit-button"
                                                                                        type="text"
                                                                                        shape="square"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleEditClick(renderIndex, expandedItem);
                                                                                        }}
                                                                                    >
                                                                                        <EditOutlined />
                                                                                    </Button>
                                                                                </Tooltip>
                                                                                <Tooltip title={<span className="manrope f-12 white">Compare with silo</span>}>
                                                                                    <Button
                                                                                        className="action-button compare-button"
                                                                                        type="text"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleCompareClick(renderIndex, expandedItem);
                                                                                        }}
                                                                                    >
                                                                                        <img src={'/img/ai_creator/compare.svg'} />
                                                                                    </Button>
                                                                                </Tooltip>
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </Card>
                                                                )}
                                                            </div>
                                                        </Col>
                                                    );
                                                })}
                                        </Row>

                                        {getFilteredExpandedRenders(expandedItem, expandedFilterKey).length === 0 && (
                                            <div className="ai-expanded-empty manrope f-14 w-500 black-55">
                                                No images found for this filter.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <>
                            {filteredActivityImages?.map((item, index) => (
                                <Row 
                                    key={index}
                                    data-activity-row={index}
                                    ref={(el) => {
                                        // Attach observer for virtualization
                                        setRowRef(el, index);
                                        // Also attach lastItemRef for infinite scroll on last row
                                        if (index === filteredActivityImages.length - 1) {
                                            lastItemRef.current = el;
                                        }
                                    }}
                                    className="justify-in-start" 
                                    style={{ position: 'relative', width: `100%`, minHeight: shouldRenderRow(index) ? 'auto' : ESTIMATED_ROW_HEIGHT }}
                                >
                                    {shouldRenderRow(index) ? (
                                    <>
                                    <Col span={4} className="activity-info-column" style={{ position: 'absolute', top: 0, left: 0, bottom: (item.status == 'inprogress' || (item.status == 'completed' && getRendersList(item).filter(r => r !== null).length === 0)) ? 110 : 40 }}>
                                        <div className="activity-info-wrapper">
                                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                                                <div className="w-100" style={{display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '15px', flexShrink: 0, width: '100%'}}>
                                                        {(() => {
                                                            const propsData = (getPropImageUris(item) || []).slice(0, 3); // Limit to max 3 props
                                                            const hasProps = propsData.length > 0;
                                                            const hasInspiration = !!item.inspiration_image_uri;
                                                            const propsCount = propsData.length;
                                                            const baseURL = item.platform ? ENVIRONMENT.getBaseURL(item.platform) : ENVIRONMENT.getBaseURL();
                                                            const siloUri = item.input_image_uri.includes('https://') ? item.input_image_uri : baseURL + item.input_image_uri;
                                                            const siloThumbUri = item.input_image_thumbnail ? (item.input_image_thumbnail.includes('https://') ? item.input_image_thumbnail : baseURL + item.input_image_thumbnail) : siloUri;
                                                            const inspirationUri = item.inspiration_image_uri ? (item.inspiration_image_uri.includes('https://') ? item.inspiration_image_uri : baseURL + item.inspiration_image_uri) : null;
                                                            const inspirationThumbUri = item.inspiration_image_thumbnail ? (item.inspiration_image_thumbnail.includes('https://') ? item.inspiration_image_thumbnail : baseURL + item.inspiration_image_thumbnail) : inspirationUri;
                                                            
                                                            // Calculate total image count: silo (1) + inspiration (0 or 1) + props (0-3)
                                                            const totalImageCount = 1 + (hasInspiration ? 1 : 0) + propsCount;
                                                            
                                                            // Card style for single row (1-2 images)
                                                            const singleRowCardStyle = {
                                                                aspectRatio: '1 / 1',
                                                                minHeight: '80px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                flex: totalImageCount === 1 ? '0 0 80%' : '0 0 calc((100% - 8px) / 2)',
                                                                boxSizing: 'border-box'
                                                            };
                                                            
                                                            // Card style for 2-row layout (3-5 images)
                                                            // For 2 cards in a row: calc((100% - 8px) / 2) - 1 gap
                                                            // For 3 cards in a row: calc((100% - 16px) / 3) - 2 gaps
                                                            const twoRowCardStyle = {
                                                                aspectRatio: '1 / 1',
                                                                minHeight: '80px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                flex: '0 0 calc((100% - 8px) / 2)',
                                                                boxSizing: 'border-box'
                                                            };
                                                            
                                                            const threeRowCardStyle = {
                                                                aspectRatio: '1 / 1',
                                                                minHeight: '80px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                flex: '0 0 calc((100% - 16px) / 3)',
                                                                boxSizing: 'border-box'
                                                            };
                                                            
                                                            // Smaller card styles for second row to prevent cutting
                                                            const secondRowTwoCardStyle = {
                                                                aspectRatio: '1 / 1',
                                                                minHeight: '60px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                flex: '0 0 calc(34% - 4px)',
                                                                maxWidth: 'calc(34% - 4px)',
                                                                boxSizing: 'border-box'
                                                            };
                                                            
                                                            const secondRowThreeCardStyle = {
                                                                aspectRatio: '1 / 1',
                                                                minHeight: '60px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                flex: '0 0 calc(32% - 5px)',
                                                                maxWidth: 'calc(32% - 5px)',
                                                                boxSizing: 'border-box'
                                                            };
                                                            
                                                            const imageStyle = { cursor: 'pointer', width: '100%', height: '100%', objectFit: 'cover' };
                                                            
                                                            // Render a single image card
                                                            const renderImageCard = (src, title, refKey, cardStyle, previewSrc) => (
                                                                <div style={cardStyle} key={refKey}>
                                                                    <Tooltip title={<span className="manrope f-12 white">{title}</span>}>
                                                                        <Image 
                                                                            ref={(el) => { if (el && refKey) previewImageRefs.current[refKey] = el; }}
                                                                            className="activity-product-card" 
                                                                            style={imageStyle} 
                                                                            preview={previewSrc ? { src: previewSrc, mask: null } : { mask: null }} 
                                                                            src={src} 
                                                                        />
                                                                    </Tooltip>
                                                                </div>
                                                            );
                                                            
                                                            // Render count card
                                                            const renderCountCard = (count, cardStyle, onClick) => (
                                                                <div style={{...cardStyle, backgroundColor: '#f5f5f5', border: '1px solid #d9d9d9', borderRadius: '4px', cursor: 'pointer'}} onClick={onClick}>
                                                                    <Tooltip title={<span className="manrope f-12 white">{count} more images</span>}>
                                                                        <span className="manrope f-14 w-600" style={{ color: '#276DD7' }}>+{count}</span>
                                                                    </Tooltip>
                                                                </div>
                                                            );
                                                            
                                                            // For 1-2 images: single row layout
                                                            if (totalImageCount <= 2) {
                                                                return (
                                                                    <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                                                                        {renderImageCard(siloThumbUri, 'Product Image', `${index}-0`, singleRowCardStyle, siloUri)}
                                                                        {hasInspiration && renderImageCard(inspirationThumbUri, 'Inspiration Image', `${index}-1`, singleRowCardStyle, inspirationUri)}
                                                                        {!hasInspiration && propsCount === 1 && (() => {
                                                                            const prop = propsData[0];
                                                                            const propUri = prop.image_uri;
                                                                            const thumbUri = prop.thumbnail_uri || prop.image_uri;
                                                                            return renderImageCard(
                                                                                thumbUri.includes('https://') ? thumbUri : baseURL + thumbUri,
                                                                                'Prop Image',
                                                                                `${index}-prop-0`,
                                                                                singleRowCardStyle,
                                                                                propUri.includes('https://') ? propUri : baseURL + propUri
                                                                            );
                                                                        })()}
                                                                    </div>
                                                                );
                                                            }
                                                            
                                                            // For 3-5 images: 2-row layout
                                                            // First row: always 2 images (silo + inspiration or silo + first prop)
                                                            // Second row: remaining images
                                                            const firstRowImages = [];
                                                            const secondRowImages = [];
                                                            
                                                            // Always add silo first
                                                            firstRowImages.push({ src: siloThumbUri, previewSrc: siloUri, title: 'Product Image', refKey: `${index}-0` });
                                                            
                                                            // Add inspiration or first prop to first row
                                                            if (hasInspiration) {
                                                                firstRowImages.push({ 
                                                                    src: inspirationThumbUri, 
                                                                    previewSrc: inspirationUri,
                                                                    title: 'Inspiration Image', 
                                                                    refKey: `${index}-1` 
                                                                });
                                                                // Add props to second row
                                                                propsData.forEach((prop, idx) => {
                                                                    const propUri = prop.image_uri;
                                                                    const thumbUri = prop.thumbnail_uri || prop.image_uri;
                                                                    secondRowImages.push({
                                                                        src: thumbUri.includes('https://') ? thumbUri : baseURL + thumbUri,
                                                                        previewSrc: propUri.includes('https://') ? propUri : baseURL + propUri,
                                                                        title: `Prop Image ${idx + 1}`,
                                                                        refKey: `${index}-prop-${idx}`
                                                                    });
                                                                });
                                                            } else {
                                                                // No inspiration, add first prop to first row
                                                                if (propsCount > 0) {
                                                                    const firstProp = propsData[0];
                                                                    const propUri = firstProp.image_uri;
                                                                    const thumbUri = firstProp.thumbnail_uri || firstProp.image_uri;
                                                                    firstRowImages.push({
                                                                        src: thumbUri.includes('https://') ? thumbUri : baseURL + thumbUri,
                                                                        previewSrc: propUri.includes('https://') ? propUri : baseURL + propUri,
                                                                        title: 'Prop Image',
                                                                        refKey: `${index}-prop-0`
                                                                    });
                                                                    // Add remaining props to second row
                                                                    propsData.slice(1).forEach((prop, idx) => {
                                                                        const pUri = prop.image_uri;
                                                                        const tUri = prop.thumbnail_uri || prop.image_uri;
                                                                        secondRowImages.push({
                                                                            src: tUri.includes('https://') ? tUri : baseURL + tUri,
                                                                            previewSrc: pUri.includes('https://') ? pUri : baseURL + pUri,
                                                                            title: `Prop Image ${idx + 2}`,
                                                                            refKey: `${index}-prop-${idx + 1}`
                                                                        });
                                                                    });
                                                                }
                                                            }
                                                            
                                                            // For 5 images: show 2 in first row, 3 in second row
                                                            // For 3-4 images: show 2 in first row, remaining in second row
                                                            const secondRowDisplayCount = totalImageCount === 5 ? 3 : secondRowImages.length;
                                                            const remainingCount = secondRowImages.length > secondRowDisplayCount 
                                                                ? secondRowImages.length - secondRowDisplayCount 
                                                                : 0;
                                                            
                                                            // Determine card style for second row based on number of items
                                                            // Use smaller styles for second row to prevent cutting
                                                            const secondRowCardStyle = secondRowDisplayCount === 3 ? secondRowThreeCardStyle : secondRowTwoCardStyle;
                                                            
                                                            return (
                                                                <>
                                                                    {/* First row: always 2 images */}
                                                                    <div style={{ display: 'flex', gap: '8px', width: '100%', marginBottom: '8px', flexShrink: 0 }}>
                                                                        {firstRowImages.map(img => renderImageCard(img.src, img.title, img.refKey, twoRowCardStyle, img.previewSrc))}
                                                                    </div>
                                                                    
                                                                    {/* Second row: remaining images */}
                                                                    <div style={{ display: 'flex', gap: '8px', width: '100%', flexShrink: 0 }}>
                                                                        {secondRowImages.slice(0, secondRowDisplayCount).map(img => 
                                                                            renderImageCard(img.src, img.title, img.refKey, secondRowCardStyle, img.previewSrc)
                                                                        )}
                                                                        {remainingCount > 0 && renderCountCard(
                                                                            remainingCount,
                                                                            secondRowCardStyle,
                                                                            () => handlePlaceholderClick(item, index)
                                                                        )}
                                                                    </div>
                                                                    
                                                                    {/* Hidden images for preview group (all props beyond displayed) */}
                                                                    {getPropImageUris(item) && getPropImageUris(item).length > propsCount && (
                                                                        getPropImageUris(item).slice(propsCount).map((prop, idx) => {
                                                                            const propUri = prop.image_uri;
                                                                            return (
                                                                                <Image 
                                                                                    key={`hidden-prop-${idx + propsCount}`}
                                                                                    style={{ display: 'none' }} 
                                                                                    preview={{ mask: null }} 
                                                                                    src={propUri.includes('https://') ? propUri : baseURL + propUri} 
                                                                                />
                                                                            );
                                                                        })
                                                                    )}
                                                                </>
                                                            );
                                                        })()}
                                                </div>
                                            </div>
                                            
                                            {item.status == 'completed' || item.status == 'failed' ?
                                            <div className="activity-action-buttons-container" style={{marginTop: 16}}>
                                                <Tooltip title={<span className="manrope f-12 white">Generate more</span>}>
                                                    <p className="manrope f-12 w-600 black-d9 mr-8 mb-0 cursor-pointer activity-action-button"
                                                        onClick={() => redirectToGenerateMore(item)}>
                                                        <RedoOutlined /></p>
                                                </Tooltip>

                                                <Tooltip title={<span className="manrope f-12 white">Product details</span>}>
                                                    <p className="manrope f-12 w-600 black-d9 mr-8 cursor-pointer mb-0 activity-action-button"
                                                        onClick={() => window.location.href = ('/products/' + item.product_id)}>
                                                        <UnorderedListOutlined />
                                                    </p>
                                                </Tooltip>

                                                <Tooltip title={<span className="manrope f-12 white">View all</span>}>
                                                    <p className="manrope f-12 w-600 black-d9 cursor-pointer mb-0 activity-action-button"
                                                        onClick={() => handleExpandJob(item.job_id)}>
                                                        <AppstoreOutlined /> View all ({getRendersList(item).filter(r => r && r.uri).length})
                                                    </p>
                                                </Tooltip>
                                            </div> : null}
                                        </div>
                                    </Col>
                                    <Col className="justify-in-start" span={20} offset={4} style={{ paddingLeft: item.status == 'failed' ? 30 : 20 , flexDirection: 'column', paddingRight: item.status == 'failed' ? 20 : 0 }}>
                                        
                                        {/* Unified Image/Loader Display */}
                                        {(item.status !== 'failed' || getRendersList(item).filter(r => r && r.uri).length > 0) && (
                                            (() => {
                                                // Use filtered renders when searching, otherwise all renders
                                                const renders = getRendersList(item);
                                                const totalImagesCount = renders.filter(r => r && r.uri).length;
                                                
                                                // Count total edit requests for descending order numbering
                                                const totalEditRequests = renders.filter(r => r && r.request_type == 'edit_request').length;
                                                
                                                // 1. Map all available renders to components (or null if invalid)
                                                const renderElements = renders.map((renderObj, renderIndex) => {
                                                    if (!renderObj || !renderObj.uri) return null;
                                                    
                                                    // Find the actual index of this render in the full item.renders array
                                                    // This is important when searching, as renderIndex refers to filteredRenders
                                                    const actualRenderIndex = (item.renders || []).findIndex(r => 
                                                        r?.render_id === renderObj?.render_id
                                                    );
                                                    
                                                    const engineVal = renderObj?.engine || item.engine;
                                                    const engineDisplayName = getDisplayEngineName(engineVal);
                                                    const engineClass = getEngineCssClass(engineVal);
                                                    const engineLogo = getEngineLogo(engineVal);
                                                    const orientation = getImageOrientation(renderObj.width, renderObj.height);

                                                    if (renderObj.asset_type === 'ai_video') {
                                                         return (
                                                             <div className={`activity-image-card-wrapper ${orientation}`} key={renderIndex}>
                                                                 <Card className={`your-activity-card ${engineClass}`} onClick={() => {
                                                                        handlePreviewClick(actualRenderIndex, item);
                                                                    }}>
                                                                        <div className="activity-video-thumbnail-container" style={{ position: 'relative', width: '100%', height: '100%' }}>
                                                                            <CustomImage
                                                                                src={ENVIRONMENT.getBaseURL() + renderObj.thumbnail} 
                                                                                preview={false}
                                                                                className={`your-activity-img ${engineClass}`}
                                                                            />
                                                                            <div className="ai-video-play-overlay">
                                                                                <img src={playIconSvg} alt="Play" className="ai-video-play-icon" />
                                                                            </div>
                                                                            {renderObj.video_number && (
                                                                                <div className="engine-logo-badge">
                                                                                    <span className="engine-name">Video {renderObj.video_number}</span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </Card>
                                                             </div>
                                                         );
                                                     }

                                                    return (
                                                        <div className={`activity-image-card-wrapper ${orientation}`} key={renderIndex}>
                                                            <Card className={`your-activity-card ${engineClass}`} onClick={(e) => {
                                                                            const render = item.renders[actualRenderIndex]
                                                                            if(render.review_status == 'rejected') {
                                                                                let prompt_text = item?.prompt_text;
                                                                                const toBase64Url = (uint8) => {
                                                                                    let binary = '';
                                                                                    for (let i = 0; i < uint8.length; i++) binary += String.fromCharCode(uint8[i]);
                                                                                    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
                                                                                };
                                                                                const compressState = (obj) => {
                                                                                    const json = JSON.stringify(obj || {});
                                                                                    const deflated = pako.deflate(json);
                                                                                    return toBase64Url(deflated);
                                                                                };
                                                                                const collaborateState = {
                                                                                    img: item.input_image_uri || "",
                                                                                    imgInspection: item.inspiration_image_uri || "",
                                                                                    inspiration_id: item.inspiration_id || null,
                                                                                    prompt: item.prompt_text || "",
                                                                                    props: item.props || [],
                                                                                };
                                                                                const collaborateParams = new URLSearchParams();
                                                                                collaborateParams.set("ai_image", "true");
                                                                                collaborateParams.set("productId", item.product_id);
                                                                                if (item.job_id) collaborateParams.set("job_id", String(item.job_id));
                                                                                if (item.inspiration_id != null) collaborateParams.set("inspiration_id", String(item.inspiration_id));
                                                                                collaborateParams.set("state", compressState(collaborateState));
                                                                                window.location.href = `/collaborate-tool/${render.collab_id}?${collaborateParams.toString()}`;
                                                                            }
                                                                            else{
                                                                                handlePreviewClick(actualRenderIndex, item);
                                                                            }
                                                                        }}>
                                                                    <CustomImage
                                                                        src={ENVIRONMENT.getBaseURL() + renderObj.uri.replace(/(\.[^.]+)$/, "_512$1")} 
                                                                        preview={false}
                                                                        className={`your-activity-img ${engineClass}`}
                                                                    />
                                                                {
                                                                    renderObj.review_status == 'rejected' ? (
                                                                        <div className="engine-logo-badge red">
                                                                            <span className="engine-name">Rejected</span>
                                                                        </div>
                                                                    ): renderObj.review_status == 'pending' ? (
                                                                        <div className="engine-logo-badge blue">
                                                                            <span className="engine-name">In Review</span>
                                                                        </div>
                                                                    ) : renderObj.review_status == 'approved' ? (
                                                                        <div className="engine-logo-badge green">
                                                                            <span className="engine-name">Approved</span>
                                                                        </div>
                                                                    ) :
                                                                renderObj.request_type == 'edit_request' ? (
                                                                    <div className="engine-logo-badge">
                                                                        <span className="engine-name">Edit {renderObj.edit_number}</span>
                                                                    </div>
                                                                ) : renderObj.request_type == 'upscale_request' ? (
                                                                    <div className="engine-logo-badge">
                                                                        <span className="engine-name">Final {renderObj.final_number}</span>
                                                                    </div>
                                                                ) : (renderObj.request_type == 'new_request' || renderObj.request_type == 'more_generations') ? (
                                                                    <div className="engine-logo-badge">
                                                                        <span className="engine-name">Base {renderObj.base_number}</span>
                                                                    </div>
                                                                ) : engineVal?.toLowerCase() === 'qwen' ? (
                                                                    <div className="engine-logo-badge proprietary-badge">
                                                                        <img src={engineLogo} alt={engineDisplayName} />
                                                                        <span className="engine-name">Poppy</span>
                                                                    </div>
                                                                ) : engineLogo && (
                                                                    <div className="engine-logo-badge">
                                                                        <img src={engineLogo} alt={engineDisplayName} />
                                                                        <span className="engine-name">{engineDisplayName}</span>
                                                                    </div>
                                                                )}
                                                                {/* Portrait layout: left buttons stay at top, right buttons split into rows */}
                                                                {orientation === 'portrait' ? (
                                                                    <>
                                                                        <div className={`image-action-buttons ${item.renders[actualRenderIndex].individual_annotation_count > 0 || item.renders[actualRenderIndex].individual_collab_id ? 'always-visible': ''} left`}>
                                                                            <Tooltip title={
                                                                                <>
                                                                                <span className="manrope f-12 white">Collaborate</span>
                                                                                <br />
                                                                                {item.renders[actualRenderIndex].individual_annotation_count > 0 
                                                                                ? "Total Comments: " + item.renders[actualRenderIndex].individual_annotation_count : ""}
                                                                                </>
                                                                            }>
                                                                                <Button 
                                                                                    className={`action-button edit-button ${item.renders[actualRenderIndex].individual_annotation_count > 0 ? 'annotation': item.renders[actualRenderIndex].individual_collab_id ? 'no-annotation' : ''}`}
                                                                                    type="text"
                                                                                    shape="circle"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleCollaborate(actualRenderIndex, item);
                                                                                    }}
                                                                                >
                                                                                    <div style={{display: 'flex', alignItems: 'center'}}>
                                                                                    {collaborateLoader ?
                                                                                        <LoadingOutlined style={{ color: '#fff', fontSize: '16px' }} /> :
                                                                                        <img src={item.renders[actualRenderIndex].individual_annotation_count > 0 || item.renders[actualRenderIndex].individual_collab_id ? "/img/collaborate_tool/new-new-collab.svg" : "/img/collaborate_tool/new-new-collab-white.svg"} style={{width: '18px', height: '13px'}} />
                                                                                    }
                                                                                    {       
                                                                                        item.renders[actualRenderIndex].individual_annotation_count > 0 ?
                                                                                        <span className="manrope f-14 w-500" style={{marginLeft: 8}}>{item.renders[actualRenderIndex].individual_annotation_count}</span>
                                                                                        : ""
                                                                                    }
                                                                                    </div>
                                                                                </Button>
                                                                            </Tooltip>
                                                                            <Tooltip title={<span className="manrope f-12 white">Submit for review</span>}>
                                                                                <Button
                                                                                    className="action-button review-button"
                                                                                    type="text"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleSubmitForReview(actualRenderIndex, item);
                                                                                    }}
                                                                                >
                                                                                    {submitForReviewLoaderId === `${item.product_id}_${item.renders[actualRenderIndex].render_id}` ?
                                                                                        <LoadingOutlined style={{ color: '#fff', fontSize: '16px' }} /> :
                                                                                        <img src={'/img/ai_creator/submit_for_review_white.svg'}/>
                                                                                    }
                                                                                </Button>
                                                                            </Tooltip>
                                                                        </div>
                                                                        {renderObj.request_type == 'upscale_request' ? (
                                                                            <>
                                                                                <div className="image-action-buttons" style={{right: '8px', top: '8px'}}>
                                                                                    <Tooltip title={<span className="manrope f-12 white">Generate Video</span>}>
                                                                                        <Button
                                                                                            className="action-button preview-button"
                                                                                            type="text"
                                                                                            shape="circle"
                                                                                            onClick={(e) => handleGenerateVideoClick(renderObj, e)}
                                                                                        >
                                                                                            <img src="/img/white-video-camera-icon.svg" style={{width: '18px', height: '13px'}} alt="Video" />
                                                                                        </Button>
                                                                                    </Tooltip>
                                                                                    <Tooltip title={<span className="manrope f-12 white">Edit image</span>}>
                                                                                        <Button
                                                                                            className="action-button edit-button"
                                                                                            type="text"
                                                                                            shape="square"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                handleEditClick(actualRenderIndex, item);
                                                                                            }}
                                                                                        >
                                                                                            <EditOutlined />
                                                                                        </Button>
                                                                                    </Tooltip>
                                                                                </div>
                                                                                <div className="image-action-buttons" style={{right: '8px', top: '48px'}}>
                                                                                    <Tooltip title={<span className="manrope f-12 white">Compare with silo</span>}>
                                                                                        <Button
                                                                                            className="action-button compare-button"
                                                                                            type="text"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                handleCompareClick(actualRenderIndex, item);
                                                                                            }}
                                                                                        >
                                                                                            <img src={'/img/ai_creator/compare.svg'}/>
                                                                                        </Button>
                                                                                    </Tooltip>
                                                                                </div>
                                                                            </>
                                                                        ) : (
                                                                            <div className="image-action-buttons" style={{right: '8px', top: '8px'}}>
                                                                                <Tooltip title={<span className="manrope f-12 white">Edit image</span>}>
                                                                                    <Button
                                                                                        className="action-button edit-button"
                                                                                        type="text"
                                                                                        shape="square"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleEditClick(actualRenderIndex, item);
                                                                                        }}
                                                                                    >
                                                                                        <EditOutlined />
                                                                                    </Button>
                                                                                </Tooltip>
                                                                                <Tooltip title={<span className="manrope f-12 white">Compare with silo</span>}>
                                                                                    <Button
                                                                                        className="action-button compare-button"
                                                                                        type="text"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            handleCompareClick(actualRenderIndex, item);
                                                                                        }}
                                                                                    >
                                                                                        <img src={'/img/ai_creator/compare.svg'}/>
                                                                                    </Button>
                                                                                </Tooltip>
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    /* Standard layout for landscape/square */
                                                                    <>
                                                                        <div className={`image-action-buttons ${item.renders[actualRenderIndex].individual_annotation_count > 0 || item.renders[actualRenderIndex].individual_collab_id ? 'always-visible': ''} left`}>
                                                                            <Tooltip title=
                                                                            {
                                                                            <>
                                                                            <span className="manrope f-12 white">Collaborate</span>
                                                                            <br />
                                                                            {item.renders[actualRenderIndex].individual_annotation_count > 0 
                                                                            ? "Total Comments: " + item.renders[actualRenderIndex].individual_annotation_count : ""}
                                                                            </>
                                                                            }>
                                                                                <Button 
                                                                                    className={`action-button edit-button ${item.renders[actualRenderIndex].individual_annotation_count > 0 ? 'annotation': item.renders[actualRenderIndex].individual_collab_id ? 'no-annotation' : ''}`}
                                                                                    type="text"
                                                                                    shape="circle"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleCollaborate(actualRenderIndex, item);
                                                                                    }}
                                                                                >
                                                                                    <div style={{display: 'flex', alignItems: 'center'}}>
                                                                                    {collaborateLoader ?
                                                                                        <LoadingOutlined style={{ color: '#fff', fontSize: '16px' }} /> :
                                                                                        <img src={item.renders[actualRenderIndex].individual_annotation_count > 0 || item.renders[actualRenderIndex].individual_collab_id ? "/img/collaborate_tool/new-new-collab.svg" : "/img/collaborate_tool/new-new-collab-white.svg"} style={{width: '18px', height: '13px'}} />
                                                                                    }
                                                                                    {       
                                                                                        item.renders[actualRenderIndex].individual_annotation_count > 0 ?
                                                                                        <span className="manrope f-14 w-500" style={{marginLeft: 8}}>{item.renders[actualRenderIndex].individual_annotation_count}</span>
                                                                                        : ""
                                                                                    }
                                                                                    </div>
                                                                                </Button>
                                                                            </Tooltip>
                                                                            <Tooltip title={<span className="manrope f-12 white">Submit for review</span>}>
                                                                                <Button
                                                                                    className="action-button review-button"
                                                                                    type="text"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleSubmitForReview(actualRenderIndex, item);
                                                                                    }}
                                                                                >
                                                                                    {submitForReviewLoaderId === `${item.product_id}_${item.renders[actualRenderIndex].render_id}` ?
                                                                                        <LoadingOutlined style={{ color: '#fff', fontSize: '16px' }} /> :
                                                                                        <img src={'/img/ai_creator/submit_for_review_white.svg'}/>
                                                                                    }
                                                                                </Button>
                                                                            </Tooltip>
                                                                        </div>
                                                                        <div className="image-action-buttons right">
                                                                            {renderObj.request_type == 'upscale_request' ? (
                                                                                <Tooltip title={<span className="manrope f-12 white">Generate Video</span>}>
                                                                                    <Button
                                                                                        className="action-button preview-button"
                                                                                        type="text"
                                                                                        shape="circle"
                                                                                        onClick={(e) => handleGenerateVideoClick(renderObj, e)}
                                                                                    >
                                                                                        <img src="/img/white-video-camera-icon.svg" style={{width: '18px', height: '13px'}} alt="Video" />
                                                                                    </Button>
                                                                                </Tooltip>
                                                                            ) : ""}
                                                                            <Tooltip title={<span className="manrope f-12 white">Edit image</span>}>
                                                                                <Button 
                                                                                    className="action-button edit-button"
                                                                                    type="text"
                                                                                    shape="square"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleEditClick(actualRenderIndex, item);
                                                                                    }}
                                                                                >
                                                                                    <EditOutlined />
                                                                                </Button>
                                                                            </Tooltip>
                                                                            <Tooltip title={<span className="manrope f-12 white">Compare with silo</span>}>
                                                                                <Button 
                                                                                    className="action-button compare-button"
                                                                                    type="text"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleCompareClick(actualRenderIndex, item);
                                                                                    }}
                                                                                >
                                                                                    <img src={'/img/ai_creator/compare.svg'}/>
                                                                                </Button>
                                                                            </Tooltip>
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </Card>
                                                        </div>
                                                    );
                                                });
                                                
                                                // 2. Count valid images
                                                const validRenderCount = renderElements.filter(el => el).length;

                                                // 3. Determine if we need loaders
                                                //    - If status is 'inprogress', we show placeholder cards based on the *latest* request batch
                                                //      (item.latest_job_id). This allows older requests with many existing renders
                                                //      to still show "in progress" placeholders for the newest generation.
                                                //    - If status is 'completed' but NO images (weird case), show loaders.
                                                const latestRequestId = item.latest_job_id ?? item.job_id ?? null;
                                                const latestRequestIdStr = latestRequestId != null ? String(latestRequestId) : null;
                                                const latestBatchRenderCount = latestRequestIdStr
                                                    ? renders.filter(r => r && r.uri && String(r.request_id) === latestRequestIdStr).length
                                                    : validRenderCount;

                                                const latestJobStatus = item.latest_status ?? item.status;
                                                const shouldShowLoaders = latestJobStatus === 'inprogress' && latestBatchRenderCount < 5;
                                                
                                                // Calculate how many loaders to show.
                                                // We target 5 cards for the latest generation batch (e.g., if 2 are done, show 3 loaders).
                                                const loaderCount = shouldShowLoaders
                                                    ? Math.max(0, 5 - latestBatchRenderCount)
                                                    : 0;

                                                // If absolutely nothing to show, return null
                                                if (validRenderCount === 0 && loaderCount === 0) return null;

                                                return (
                                                    <div className="activity-images-wrapper">
                                                        <div style={{ position: 'relative' }}>
                                                            <div
                                                                className="activity-images-scroll-container"
                                                                ref={(el) => {
                                                                    rowRefs.current[index] = el;
                                                                    if (el && !scrollPositions[index]) {
                                                                        setTimeout(() => updateScrollPosition(index, validRenderCount), 100);
                                                                    }
                                                                }}
                                                                onScroll={() => updateScrollPosition(index, validRenderCount)}
                                                            >
                                                                <Row className="activity-images-row" style={{ flexWrap: 'nowrap' }}>
                                                                    {/* Show loaders first (so in-progress placeholders appear at the start) */}
                                                                    {[...Array(loaderCount)].map((_, idx) => (
                                                                        <div className="activity-image-card-wrapper" key={`loader-${idx}`}>
                                                                            <Card style={{cursor: 'auto'}} className="your-activity-card border loading">
                                                                                <LoadingOutlined />
                                                                            </Card>
                                                                        </div>
                                                                    ))}

                                                                    {/* Then show valid images - lazy loaded */}
                                                                    {renderElements.slice(0, getVisibleRenderCount(index))}
                                                                </Row>
                                                            </div>
                                                            {isScrollable(index) && (
                                                                <>
                                                                    <Button
                                                                        className="activity-nav-arrow-btn left"
                                                                        icon={<ArrowLeftOutlined />}
                                                                        onClick={() => handleScrollLeft(index)}
                                                                        disabled={!canScrollLeft(index)}
                                                                    />
                                                                    <Button
                                                                        className="activity-nav-arrow-btn right"
                                                                        icon={<ArrowRightOutlined />}
                                                                        onClick={() => handleScrollRight(index, validRenderCount)}
                                                                        disabled={!canScrollRight(index)}
                                                                    />
                                                                </>
                                                            )}
                                                        </div>
                                                        {(item.latest_status ?? item.status) == 'inprogress' || ((item.latest_status ?? item.status) == 'completed' && getRendersList(item).filter(r => r !== null).length === 0) ?
                                                            <Col span={24} offset={0} className="activity-progress" style={{ padding: 0, marginTop: 16 }}>
                                                                <span className="manrope f-12 w-600 black-d9">{getProgress(item.submitted_on)}% Complete</span>
                                                                <Progress
                                                                    strokeColor={{
                                                                        "0%": "#7883DC",
                                                                        "50%": "#276DD7",
                                                                        "100%": "#91D5FF",
                                                                    }}
                                                                    className="activity-progress-indicator"
                                                                    percent={getProgress(item.submitted_on)}
                                                                />
                                                            </Col> : ''}
                                                    </div>
                                                );
                                            })()
                                        )}
                                        {/* failed jobs, show a single div with remove and retry options */}
                                        {item.status == 'failed' && getRendersList(item).filter(r => r && r.uri).length === 0 ?
                                            <div className="failed-ai-job-container">
                                                <div className="failed-ai-job-textual-content">
                                                    <HistoryOutlined style={{fontSize: 24}}/>
                                                    <div className="manrope f-16 w-700">Your image request has timed out.</div>
                                                </div>

                                                <div className="failed-ai-job-button-container mt-16">
                                                    <Button className="modal-okay-gray ai-failed-job-button square font-14" style={{color: "black" }} onClick={() => regenerateRequest(item.job_id)} disabled={regenerateRequestLoaderId == item.job_id} >
                                                        {regenerateRequestLoaderId == item.job_id ? <LoadingOutlined /> : <RedoOutlined />}
                                                        Try again
                                                    </Button>
                                                    <Button className="modal-okay-gray square font-14 ai-failed-job-button remove-job" style={{color: "#D93025" }} onClick={() => removeRequest(item.job_id)} disabled={removeRequestLoaderId == item.job_id}>
                                                        {removeRequestLoaderId == item.job_id ? <LoadingOutlined /> : <DeleteOutlined />}
                                                        Delete
                                                    </Button>
                                                </div>
                                            </div>
                                         : '' }
                                    </Col>
                                    <Divider type="horizontal" />
                                    </>
                                    ) : (
                                        <div 
                                            className="virtualized-row-placeholder" 
                                            style={{ height: ESTIMATED_ROW_HEIGHT, width: '100%', background: '#fafafa', borderRadius: 4 }} 
                                        />
                                    )}
                                </Row>
                            ))}
                            
                            {/* Empty state for search with no results */}
                            {props.searchQuery && props.searchQuery.trim() !== '' && filteredActivityImages?.length === 0 && !props.yourActivityLoading && (
                                <div className="justify-in-center" style={{ padding: '40px 20px', textAlign: 'center' }}>
                                    <Empty 
                                        description={
                                            <div>
                                                <p className="manrope f-16 w-500 black-55">No results found for "{props.searchQuery}"</p>
                                                {props.isSearchFetchingAll ? (
                                                    <p className="manrope f-14 black-73">
                                                        <LoadingOutlined style={{ marginRight: 8 }} />
                                                        Searching all items...
                                                    </p>
                                                ) : (
                                                    <p className="manrope f-14 black-73">Try a different search term</p>
                                                )}
                                            </div>
                                        }
                                    />
                                </div>
                            )}
                            
                            {/* Show loading spinner only when not searching and loading more */}
                            {props.isLoadingMoreData && !props.searchQuery && (
                                <div className="justify-in-center" style={{ padding: '20px 0' }}>
                                    <Spin indicator={antIcon} tip={<span className='manrope f-16' style={{ color: '#276DD7' }}>Loading more AI requests</span>} />
                                </div>
                            )}
                            
                            {/* Show search loading indicator */}
                            {props.searchQuery && props.isSearchFetchingAll && filteredActivityImages?.length > 0 && (
                                <div className="justify-in-center" style={{ padding: '20px 0' }}>
                                    <Spin indicator={antIcon} tip={<span className='manrope f-16' style={{ color: '#276DD7' }}>Searching all items...</span>} />
                                </div>
                            )}
                                </>
                            )}
                            </>
                        }
                    </TabPane>
                    )}
                    {props.bulkRequests?.some(r => r.status === 'completed') && (
                    <TabPane
                        tab={
                            <span 
                                className={`manrope f-14 w-700 asset-tab-heading`} 
                                style={{ color: resolvedActiveTabKey === 'bulk_history' ? '#276DD7' : '#000000', display: 'flex', alignItems: 'center' }}
                            >
                                <FileOutlined className="mr-2" style={{ fontSize: 16 }} />
                                Bulk Upload History
                            </span>
                        }
                        key={'bulk_history'}>
                        <div style={{ padding: '0 10px', minHeight: '100vh' }}>
                            {(() => {
                                const baseURL = ENVIRONMENT.getBaseURL();
                                const completedRequests = props.bulkRequests?.filter(r => r.status === 'completed') || [];
                                
                                return (
                                    <>
                                        {completedRequests.map((req) => (
                                            <div key={req.id}>
                                                <Row gutter={24} align="middle" style={{ padding: '24px 0' }}>
                                                    <Col span={5}>
                                                        <div style={{ display: 'flex', gap: 8 }}>
                                                            {(req.thumbnails || []).slice(0, 3).map((imgStr, i) => {
                                                                const imgSrc = imgStr ? (imgStr.includes('http') ? imgStr : baseURL + imgStr) : null;
                                                                if (!imgSrc) return null;
                                                                
                                                                return (
                                                                    <div key={i} style={{ 
                                                                        width: 80, 
                                                                        height: 80, 
                                                                        backgroundColor: '#f5f5f5', 
                                                                        borderRadius: 4, 
                                                                        border: '1px solid #E6E6E6',
                                                                        overflow: 'hidden'
                                                                    }}>
                                                                        <img src={imgSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </Col>
                                                    <Col span={5}>
                                                        <div className="manrope f-16 w-600 black-00" style={{ textAlign: 'left' }}>
                                                            {req.bulk_upload_name.length > 20 
                                                                ?req.bulk_upload_name.substring(0, 12) + '...' +req.bulk_upload_name.substring(req.bulk_upload_name.length - 8) 
                                                            :req.bulk_upload_name}
                                                        </div>
                                                    </Col>
                                                    <Col span={5}>
                                                        <div className="manrope f-14 black-45">
                                                            {req.created_at ? new Date(req.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Jan 8, 2026'}
                                                        </div>
                                                    </Col>
                                                    <Col span={4} style={{ textAlign: 'right' }}>
                                                        <Button 
                                                            type="link" 
                                                            className="manrope f-14 w-600" 
                                                            style={{ color: '#276DD7' }} 
                                                            onClick={() => {
                                                                const csvUrl = req.csv_file_uri?.includes('http') ? req.csv_file_uri : baseURL + req.csv_file_uri;
                                                                window.open(csvUrl, '_blank');
                                                            }}
                                                        >
                                                            Download CSV
                                                        </Button>
                                                    </Col>
                                                    <Col span={4} style={{ textAlign: 'right' }}>
                                                        <Button 
                                                            className="manrope f-14 w-600"
                                                            style={{borderRadius: '3px', padding: '8px 12px', display: 'inline-flex', alignItems: 'center', height: 'auto'}}
                                                            onClick={() => {
                                                                window.location.href = `/images?type=ai&bulkRequestId=${req.id}`;
                                                            }}
                                                        >
                                                            View Images
                                                        </Button>
                                                    </Col>
                                                </Row>
                                                <Divider style={{ margin: 0 }} />
                                            </div>
                                        ))}
                                        {completedRequests.length === 0 && (
                                            <div style={{ textAlign: 'center', padding: '100px 0' }} className="manrope black-45">
                                                No bulk upload history found.
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    </TabPane>
                    )}
                    <TabPane
                        tab={
                            <span
                                className={`manrope f-14 w-700 asset-tab-heading`}
                                style={{ color: resolvedActiveTabKey === 'ai-created-images' ? '#276DD7' : '#000000' }}
                            >
                                <CompassOutlined className="mr-2"/> Explore Images
                            </span>
                        }
                        key={'ai-created-images'}
                        className='pd-8'
                        forceRender={false}>
                        {USE_DYNAMIC_EXPLORE ? (
                            exploreLoading ? (
                                <AiLoader />
                            ) : (
                                <TabContent
                                    images={exploreImages}
                                    loading={exploreLoading}
                                />
                            )
                        ) : (
                            props.aiImagesLoading ? (
                                <AiLoader />
                            ) : (
                                <TabContent
                                    images={props.displayableImages}
                                    loading={props.aiImagesLoading}
                                />
                            )
                        )}
                    </TabPane>

                </Tabs>
            </Col>
        </Row>
        
        <AIEditorModalViewer 
            from="activity"
            goToNextImage={goToNextImage}
            goToPreviousImage={goToPreviousImage}
            isOpen={isModalOpen}
            onClose={handleModalClose}
            imageData={selectedImageData}
            compareSiloModalOpen={compareSiloModalOpen}
            imageDisplayLoader={imageDisplayLoader}
            setCompareSiloModalOpen={setCompareSiloModalOpen}
            markFavorite={markAiImageFavorite}
            unmarkFavorite={unmarkAiImageFavorite}
            updatedRenderNameInImagesList={updatedRenderNameInImagesList}
            renderDelete={renderDelete}
            handleSubmitForReview={handleSubmitForReview}
            handleCollaborate={handleCollaborate}
        />

        {/* Standalone Compare Modal - opened directly from image cards */}
        {!isModalOpen && (
            <CompareSiloView
                isOpen={compareSiloModalOpen}
                onClose={closeCompareModal}
                closeAllModals={closeCompareModal}
                imageData={selectedImageData}
            />
        )}

        <GenerateAiVideoModal
            visible={generateVideoModalOpen}
            onCancel={() => setGenerateVideoModalOpen(false)}
            imageUrl={selectedVideoData ? ENVIRONMENT.getBaseURL() + selectedVideoData.uri : ''}
            image_id={selectedVideoData?.render_id}
            onSuccess={handleVideoGenerationSuccess}
        />

        {/* Edit product name modal (expanded header) */}
        <Modal
            visible={editProductNameModalOpen}
            onCancel={() => setEditProductNameModalOpen(false)}
            footer={null}
            centered
            width={520}
            destroyOnClose
        >
            <div style={{ marginTop: 8 }}>
                <h4 className="manrope f-20 w-500 black-00" style={{ marginBottom: 16 }}>
                    Edit product name
                </h4>
                <Input
                    value={productNameDraft}
                    onChange={(e) => setProductNameDraft(e.target.value)}
                    placeholder="Enter product name"
                    className="manrope f-14"
                    style={{ height: 40 }}
                    maxLength={120}
                    onPressEnter={handleSaveProductName}
                    disabled={productNameSaving}
                />
                <div className="justify-in-end" style={{ marginTop: 20 }}>
                    <Button
                        className="modal-okay-gray square font-14 create-product-modal-cancel"
                        style={{ marginRight: 10 }}
                        onClick={() => setEditProductNameModalOpen(false)}
                        disabled={productNameSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        className="modal-okay square font-14 create-product-modal-add"
                        onClick={handleSaveProductName}
                        disabled={productNameSaving}
                    >
                        {productNameSaving ? <span>Saving <LoadingOutlined spin /></span> : 'Save'}
                    </Button>
                </div>
            </div>
        </Modal>

        <Modal
            destroyOnClose={true}
            className="modal-share-success"
            width={600}
            visible={deleteModal}
            closable={false}
            footer={[
              <span className="justify-in-center">
                <Button
                  style={{ textAlign: "center" }}
                  disabled={deleteLoader}
                  className="modal-okay-gray square font-14"
                  onClick={() => setDeleteModal(undefined)}
                >
                  Cancel
                </Button>
                <Button
                  style={{ textAlign: "center" }}
                  disabled={deleteLoader}
                  className="modal-okay square font-14"
                  onClick={() => deleteImage()}
                >
                  {deleteLoader ? (
                    <span>
                      Deleting <LoadingOutlined spin />
                    </span>
                  ) : (
                    <span> Okay </span>
                  )}
                </Button>
              </span>,
            ]}
            centered={true}
          >
            <div
              style={{
                margin: "0px auto 0px auto",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <div>
                <h2 className="manrope f-16 black-00 w-600">
                  You're about to delete a {(deleteModal?.asset_type === 'ai_video' || deleteModal?.image_type === 'ai_video') ? "Video" : "Lifestyle Image"}
                </h2>
                <p className="manrope f-14 black-55">
                  This will remove your {(deleteModal?.asset_type === 'ai_video' || deleteModal?.image_type === 'ai_video') ? "video" : "lifestyle image"}. Are you sure you want to delete?
                </p>
              </div>
            </div>
          </Modal>
          
        </>
    )
}

export default React.memo(ActivityAndExploreSection);